import {
 useAuth
} from '../hooks/useAuth'
import {
  useIntegrationInstances
} from '../hooks/useIntegrationInstances'
import {
  IntegrationCategoriesSection,
} from './IntegrationCategoriesSection'
import {
 WhatsAppPairingModal
} from './WhatsAppPairingModal'
import {
 SlackPairingModal, type SlackOAuthFlow
} from './SlackPairingModal'
import {
 SlackPersonalAccessModal
} from './SlackPersonalAccessModal'
import {
 ZoomPairingModal
} from './ZoomPairingModal'
import {
  ImapConnectModal,
} from './ImapConnectModal'
import {
 ContactTextingConsent,
} from './ContactTextingConsent'
import {
 SMSPairingModal
} from './SMSPairingModal'
import {
  IMessagePairingModal
} from './IMessagePairingModal'
import {
 GranolaPairingModal
} from './GranolaPairingModal'
import {
 ClickUpPairingModal
} from './ClickUpPairingModal'
import {
 AsanaPairingModal
} from './AsanaPairingModal'
import {
 NotionPairingModal
} from './NotionPairingModal'
import {
 HubSpotPairingModal
} from './HubSpotPairingModal'
import {
 SalesforcePairingModal
} from './SalesforcePairingModal'
import {
 HoneyBookPairingModal
} from './HoneyBookPairingModal'
import {
 AttioPairingModal
} from './AttioPairingModal'
import {
 DiscoveredUrlPairingModal
} from './DiscoveredUrlPairingModal'
import {
 FathomPairingModal
} from './FathomPairingModal'
import {
 OtterPairingModal
} from './OtterPairingModal'
import {
 WisprPairingModal
} from './WisprPairingModal'
import {
 BillingSection,
} from './BillingSection'
import {
  AssistantEmailSection,
} from './AssistantEmailSection'
import {
  DedicatedNumberSection,
} from './DedicatedNumberSection'
import {
  ShortcutSection,
} from './ShortcutSection'
import {
  AnnouncementBanner,
} from './AnnouncementBanner'
import {
  PurchaseCreditsSection,
} from './PurchaseCreditsSection'
import {
  AutomaticApprovalsSection,
} from './AutomaticApprovalsSection'
import {
 ReferralStrip,
} from './ReferralStrip'
import {
 ReferralLinkModal,
} from './ReferralLinkModal'
import {
 clearReferralSpotlightIntent, hasReferralSpotlightIntent,
} from '../utils/referral'
import {
 whatsappService, slackService, slackPersonalService, teamsAccessService, zoomService, granolaService, clickupService, asanaService, notionService, hubspotService, attioService, salesforceService, honeybookService, zohoService, fathomService, otterService, wisprService, authService, billingService, smsService, imessageService, scopeService, getApiErrorStatus, getUserFacingApiErrorMessage,
} from '../services/api'
import {
 useState, useEffect
} from 'react'
import type {
  ReactElement
} from 'react'
import {
 useFeatureFlagEnabled,
} from 'posthog-js/react'
import type {
 WhatsAppStatusResponse, SlackStatusResponse, SlackPersonalStatusResponse, TeamsAccessStatusResponse, ZoomStatusResponse, GranolaStatusResponse, ClickUpStatusResponse, AsanaStatusResponse, NotionStatusResponse, HubSpotStatusResponse, AttioStatusResponse, SalesforceStatusResponse, HoneyBookStatusResponse, IntegrationStatusResponse, FathomStatusResponse, OtterStatusResponse, WisprStatusResponse, AssistantCharacter,
 SmsStatusResponse, IMessageStatusResponse, ScopeStatusResponse, ConnectedAccountSummary,
 SubscriptionResponse,
} from '../services/api'
import {
 AlertTriangle, Bot, ChevronRight, Pencil, Plus, ShieldCheck, Trash2
} from 'lucide-react'
import {
 useLocation
} from 'react-router-dom'
import {
 OnboardingEvents,
 OnboardingWizardEvents,
 trackEvent,
 FeatureFlags,
 getFeatureFlagVariantSync,
 flagsSettledForIdentifiedUser,
} from '../analytics/posthog'
import {
 leaveForSignIn
} from '../analytics/signInReturn'
import {
 AssistantNameEditor
} from './AssistantNameEditor'
import {
 CustomCharacterForm
} from './CustomCharacterForm'
import {
 clearCustomCharacterDraft
} from '../utils/customCharacterDraft'
import {
  formatScopeClusterList,
  missingScopeClusters,
  parseScopeClusters
} from '../utils/scopeClusters'
import type {
  ScopeCluster
} from '../utils/scopeClusters'
import {
  AssistantWaitingCTA
} from './AssistantWaitingCTA'
import {
  ACCOUNT_CARD_CLASS,
  CATEGORY_CARD_CLASS,
} from '../utils/cardStyles'
import {
  settingsMovesMailBesideMessaging
} from '../utils/settingsLayout'
import {
  addonOptInLabel,
  describeAddonChargeStart,
  isBilledSubscription,
  requiresExplicitAddonOptIn,
} from '../utils/personalInboxOptIn'
import {
  useExperimentVariant
} from '../hooks/useExperimentVariant'
import {
  CONNECT_INBOX_PARAM,
  CONNECT_INBOX_PARAM_VALUE
} from '../utils/routes'
import {
  settingsSectionAnchor
} from '../utils/settingsSections'
import {
  useSettingsSectionDeepLink
} from '../hooks/useSettingsSectionDeepLink'
import {
  SettingsNav
} from './SettingsNav'
import {
  SETTINGS_PAGES, type SettingsPageId,
} from '../utils/settingsNav'
import type {
  CatalogPart
} from './IntegrationCategoriesSection'
import {
  isChromeOnIOS
} from '../utils/inAppBrowser'

// Both add-on cards render nothing on their own terms — the dedicated number
// when the pool has none free, the credits card when the server declines the
// read — so the pair is a wrapping FLEX row, not a two-column grid: a lone
// survivor takes the whole row instead of sitting half-width beside a hole.
// `empty:hidden` is what makes that work; a wrapper around a component that
// returned null is a genuinely empty element, and hiding it takes it out of
// the flex layout entirely. Both card roots are plain block divs, so the cell
// needs nothing further to make them fill it.
//
// The row is items-start, unlike the stretched Billing/Mail grid above:
// neither of these cards has an action pinned to its foot, so stretching the
// shorter one only pads it with dead space under its last control.
const ADDON_CARD_CELL = 'min-w-0 flex-1 basis-80 empty:hidden'

// Each secondary section in its deep-link anchor, so the single page and the
// sidebar layout place the same blocks rather than two copies of them.

/** How much the assistant does without asking (GET-12229). */
function AutomaticApprovalsBlock() {
  return (
    <div className="mb-6" {...settingsSectionAnchor('automatic-approvals')}>
      <AutomaticApprovalsSection />
    </div>
  )
}

/** The owner's consent to texting their contacts from the shared guest line
 * (GET-18126); renders nothing unless guest-sms-pool is on. */
function TextingContactsBlock() {
  return (
    <div className="mb-6 empty:hidden" {...settingsSectionAnchor('texting-contacts')}>
      <ContactTextingConsent />
    </div>
  )
}

/** One press on the iPhone Action Button = a voice note to the assistant. The
 * id is also the announcement banner's jump target. */
function IphoneActionButtonBlock() {
  return (
    <div className="mb-6" {...settingsSectionAnchor('iphone-action-button')}>
      <ShortcutSection />
    </div>
  )
}

/** The dedicated assistant number ($15/mo); renders nothing when the pool has none free. */
function DedicatedNumberBlock({ className = 'mb-6 empty:hidden' }: { className?: string }) {
  return (
    <div className={className} {...settingsSectionAnchor('dedicated-number')}>
      <DedicatedNumberSection />
    </div>
  )
}

/** The prepaid balance the assistant buys from. */
function PurchaseCreditsBlock({
  show,
  className = 'mb-6 empty:hidden',
}: {
  show: boolean
  className?: string
}) {
  return (
    <div className={className} {...settingsSectionAnchor('purchase-credits')}>
      {show && <PurchaseCreditsSection />}
    </div>
  )
}

// Keep one mounted subtree so settings-mail-beside-messaging can move the
// sections around it without resetting DedicatedNumberSection state when
// PostHog resolves the flag.
function SecondarySettingsSections({
  showPurchaseCredits,
}: {
  showPurchaseCredits: boolean
}) {
  return (
    <>
      <AutomaticApprovalsBlock />

      {/* The two paid add-ons, side by side: a dedicated assistant phone
          number ($15/mo) and the prepaid balance the assistant buys from.
          Down here rather than up in the Billing grid because neither is the
          plan — Billing is the subscription, these are things bought on top
          of it — and because that grid is a stretched pair whose two cards
          are meant to share a bottom edge. */}
      <div className="mb-6 flex flex-wrap items-start gap-4">
        <DedicatedNumberBlock className={ADDON_CARD_CELL} />
        <PurchaseCreditsBlock show={showPurchaseCredits} className={ADDON_CARD_CELL} />
      </div>

      <TextingContactsBlock />
      <IphoneActionButtonBlock />
    </>
  )
}

interface OrderedSettingsSectionsProps {
  // settings-mail-beside-messaging 'test' takes the top grid apart, so this is
  // ONE node whose home moves, not a pair of slots: control passes the
  // Billing/Mail grid, the test arm passes Billing alone (its Mail half having
  // gone into the integrations catalog). An element plus a placement boolean
  // rather than two nullable slots — the arms differ in *what* renders as well
  // as where, which the element covers, and "exactly one of them is filled"
  // then can't be got wrong, instead of being a comment nothing enforces.
  billing: ReactElement
  billingBelowWaiting: boolean
  secondary: ReactElement
  integrations: ReactElement
  assistantWaiting: ReactElement
  assistantEmail: ReactElement
}

// Stable keys let React move the existing section nodes without remounting them,
// while keeping visual, keyboard, and screen-reader order aligned in every arm.
function OrderedSettingsSections({
  billing,
  billingBelowWaiting,
  secondary,
  integrations,
  assistantWaiting,
  assistantEmail,
}: OrderedSettingsSectionsProps) {
  const primarySections = [
    integrations,
    assistantWaiting,
    ...(billingBelowWaiting ? [billing] : []),
    assistantEmail,
  ]
  // The secondary sections always go last: the concluded
  // settings-integrations-first experiment (441502) shipped its 'test' order.
  // The mail-beside-messaging arm already carries Billing in
  // `primarySections`, so it gets no top slot.
  const sections = billingBelowWaiting
    ? [...primarySections, secondary]
    : [billing, ...primarySections, secondary]

  return <>{sections}</>
}

// Shown when the work-account connect button fails before the browser ever
// leaves for Google or Microsoft — the backend erroring, or the network being
// down. Says the attempt did not start, so the user retries rather than
// assuming the click registered and waiting for a redirect that never comes.
//
// Two constants rather than one, because the button is two actions. A Microsoft
// user is reconnecting an account that has dropped; a Google user is granting
// one more permission on an account that still works. Telling the second group
// their account needs reconnecting is simply false, and they are the default.
// Named by action, not by provider: the selector is `isGoogleUser`, a negation
// of one provider rather than an enum, so provider-shaped keys would imply a
// taxonomy the code does not have.
const MAIL_ACCESS_START_FAILED =
  "We couldn't open Google's permission screen. Please try again in a moment."
const RECONNECT_START_FAILED =
  "We couldn't start reconnecting your account. Please try again in a moment."

const WORK_ACCOUNT_ERROR_ID = 'work-account-connect-error'

// `ConnectedAccountSummary.calendar_provider` of an IMAP inbox (GET-17286).
const IMAP_PROVIDER = 'imap'

// The personal-inbox connect, one box below, fails the same way.
const PERSONAL_INBOX_START_FAILED =
  "We couldn't start connecting that inbox. Please try again in a moment."

// An inbox that is asking for something. Shared by the row and by the order
// the list is rendered in, so the two cannot disagree about which rows matter.
const inboxNeedsAttention = (account: ConnectedAccountSummary): boolean =>
  Boolean(account.needs_reauth || account.needs_full_reconnect)

// How tall the connected-inbox list may get before it scrolls: three rows.
// A card that grows a row per inbox is a card whose height nobody can design
// around — it pushed the integrations catalog further down the page with
// every inbox connected, and stretched the Billing card beside it to match.
// Past this the list scrolls and the card's height stops moving. The value is
// three single-line rows (36px each) plus HALF of a fourth, deliberately: an
// exact multiple would hide a fourth inbox behind a scrollbar nobody looks
// for, and one of those hidden rows could be one asking to be reconnected.
// Three inboxes fit with no scrollbar; a fourth is what starts the scroll.
const INBOX_LIST_MAX_HEIGHT = 'max-h-[7.875rem]'

// One connected inbox. Healthy is a dot rather than a "Connected" pill: it is
// the common case and says nothing an absent warning doesn't, while costing
// ~85px of a row that also has to hold an address and a Disconnect. The
// states that need words still get them.
function ConnectedInboxRow({
  account,
  busy,
  onReconnect,
  onDisconnect,
}: {
  account: ConnectedAccountSummary
  busy: boolean
  onReconnect: () => void
  onDisconnect: () => void
}): JSX.Element {
  const needsReconnect = inboxNeedsAttention(account)
  return (
    <li className="flex items-center justify-between gap-3 py-2">
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
        {needsReconnect ? (
          <span className="inline-flex flex-shrink-0 items-center px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
            {account.needs_reauth ? 'Needs reconnect' : 'Reconnect for full access'}
          </span>
        ) : (
          <span className="flex-shrink-0">
            <span className="block h-2 w-2 rounded-full bg-green-500" aria-hidden="true" />
            <span className="sr-only">Connected</span>
          </span>
        )}
        {/* break-words with min-w-0, never truncate: the control beside it
            disconnects the inbox this address names, and a phone has no
            hover to recover an ellipsis. min-w-0 is what lets the flex item
            shrink far enough for the wrap to happen at all. */}
        <span className="min-w-0 break-words text-sm text-gray-700">
          {account.email}
        </span>
      </div>
      <div className="flex flex-shrink-0 items-center gap-2">
        {needsReconnect && (
          <button
            onClick={onReconnect}
            disabled={busy}
            className="text-sm font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50"
          >
            Reconnect
          </button>
        )}
        {/* An IMAP inbox gets its calendar from the same password when its
            server has one (GET-17873); reconnecting can't add one. */}
        {!needsReconnect && !account.has_calendar && account.calendar_provider !== IMAP_PROVIDER && (
          <button
            onClick={onReconnect}
            disabled={busy}
            className="text-sm font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50"
          >
            Reconnect to add calendar
          </button>
        )}
        <button
          onClick={onDisconnect}
          className="text-sm font-medium text-gray-500 hover:text-red-600"
        >
          Disconnect
        </button>
      </div>
    </li>
  )
}

// Best-effort hostname for a tile's detail line. Discovered integrations (e.g.
// Zoho) store the full resource URL as external_user_id; show just its host.
const hostnameFromUrl = (url: string | null | undefined): string | undefined => {
  if (!url) return undefined
  try {
    return new URL(url).hostname
  } catch {
    return undefined
  }
}

// The owner's own read-only Slack connection (GET-14181): user-scope OAuth
// against its own endpoints and callback page, on the shared Slack modal.
const SLACK_PERSONAL_FLOW: SlackOAuthFlow = {
  providerName: 'SlackPersonal',
  successType: 'SLACK_PERSONAL_OAUTH_SUCCESS',
  errorType: 'SLACK_PERSONAL_OAUTH_ERROR',
  initiateOAuth: () => slackPersonalService.initiateOAuth(),
  isConnected: async () => (await slackPersonalService.getStatus()).connected,
  title: 'Connect your Slack account',
  description:
    "Let your assistant read Slack as you — your DMs, channels the Catch app isn't in, and what you haven't read yet. Read-only: it can never post or mark anything as read, and you choose which conversations it may see.",
  buttonLabel: 'Connect my Slack',
}

// Friendly names for the integration types returned via the mobile OAuth
// redirect flow (GET-8963), used in the connect-result banner. Falls back to a
// capitalized type for anything not listed.
const INTEGRATION_RETURN_LABELS: Record<string, string> = {
  hubspot: 'HubSpot',
  asana: 'Asana',
  otter: 'Otter.ai',
  wispr: 'Wispr Flow',
  clickup: 'ClickUp',
  notion: 'Notion',
  attio: 'Attio',
  salesforce: 'Salesforce',
  honeybook: 'HoneyBook',
  fathom: 'Fathom',
  granola: 'Granola',
}

const integrationReturnLabel = (type: string): string =>
  INTEGRATION_RETURN_LABELS[type] ?? type.charAt(0).toUpperCase() + type.slice(1)

export function SettingsPage() {
  const { user } = useAuth()
  // `?section=<id>` (GET-18882): read at first render, before the cleanup
  // effects below strip the query string.
  useSettingsSectionDeepLink()
  const [whatsappStatus, setWhatsappStatus] = useState<WhatsAppStatusResponse | null>(null)
  const [whatsappLoading, setWhatsappLoading] = useState(false)
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false)
  const [slackStatus, setSlackStatus] = useState<SlackStatusResponse | null>(null)
  const [slackLoading, setSlackLoading] = useState(false)
  const [showSlackModal, setShowSlackModal] = useState(false)
  // The owner's own read-only Slack connection (GET-14181).
  const [slackPersonalStatus, setSlackPersonalStatus] = useState<SlackPersonalStatusResponse | null>(null)
  const [slackPersonalLoading, setSlackPersonalLoading] = useState(false)
  const [showSlackPersonalModal, setShowSlackPersonalModal] = useState(false)
  const [showSlackPersonalAccessModal, setShowSlackPersonalAccessModal] = useState(false)
  const [teamsAccessStatus, setTeamsAccessStatus] = useState<TeamsAccessStatusResponse | null>(null)
  const [zoomStatus, setZoomStatus] = useState<ZoomStatusResponse | null>(null)
  const [zoomLoading, setZoomLoading] = useState(false)
  const [showZoomModal, setShowZoomModal] = useState(false)
  const [zoomAvailable, setZoomAvailable] = useState(false)
  const [smsStatus, setSmsStatus] = useState<SmsStatusResponse | null>(null)
  const [smsLoading, setSmsLoading] = useState(false)
  const [showSmsModal, setShowSmsModal] = useState(false)
  // Keep these values subscribed to PostHog reloads. identify() refreshes flags
  // after login, so a one-shot read can otherwise latch the cached anonymous
  // result just before the identified user's result arrives.
  const smsFeatureEnabled = useFeatureFlagEnabled(FeatureFlags.SMS_CHANNEL) === true
  const customIntegrationBuilderEnabled =
    useFeatureFlagEnabled(FeatureFlags.CUSTOM_INTEGRATION_BUILDER) === true
  const pipedreamCatalogBrowseEnabled =
    useFeatureFlagEnabled(FeatureFlags.PIPEDREAM_CATALOG_BROWSE) === true
  // Kept tri-state as well as collapsed: the announcement banner needs to tell
  // "off" apart from "not resolved yet", because it resolves once on mount.
  const personalEmailFeatureFlag = useFeatureFlagEnabled(
    FeatureFlags.CONNECTED_PERSONAL_EMAIL,
  )
  // Tri-state for the same reason: the announcement pointing at the credits
  // card resolves once on mount, so it must not read "not loaded yet" as off.
  const purchaseCreditsFlag = useFeatureFlagEnabled(
    FeatureFlags.PURCHASE_CREDITS_TOPUP,
  )
  // The location-reminders launch notice; its entry shows only on a definite
  // `true`, so the tri-state matters the other way round here.
  const locationRemindersFlag = useFeatureFlagEnabled(
    FeatureFlags.LOCATION_REMINDERS,
  )
  const personalEmailFeatureEnabled = personalEmailFeatureFlag === true
  const imapPersonalInboxEnabled =
    useFeatureFlagEnabled(FeatureFlags.IMAP_PERSONAL_INBOX) === true
  // settings-mail-beside-messaging: Mail & Calendar becomes a catalog cell
  // beside Messaging and Billing drops below the assistant-waiting card. Not
  // `useFeatureFlagVariantKey`: sign-in lands here, and that hook would enrol
  // the person in the anonymous visitor's arm first (see the hook).
  const mailBesideMessagingVariant = useExperimentVariant(
    FeatureFlags.SETTINGS_MAIL_BESIDE_MESSAGING,
  )
  // settings-sidebar: Settings as a sidebar of named pages. It places Mail and
  // Billing itself, so 462588's arms apply only without it.
  const settingsSidebarEnabled = useFeatureFlagEnabled(FeatureFlags.SETTINGS_SIDEBAR) === true
  const mailBesideMessaging = !settingsSidebarEnabled && settingsMovesMailBesideMessaging(
    mailBesideMessagingVariant,
  )
  const [imessageStatus, setImessageStatus] = useState<IMessageStatusResponse | null>(null)
  const [imessageLoading, setImessageLoading] = useState(false)
  const [showImessageModal, setShowImessageModal] = useState(false)
  const [granolaStatus, setGranolaStatus] = useState<GranolaStatusResponse | null>(null)
  const [granolaLoading, setGranolaLoading] = useState(false)
  // Connect-a-personal-inbox (read + draft) flow state.
  const [connectingPersonalEmail, setConnectingPersonalEmail] = useState(false)
  // The Personal Inbox explainer modal: opened by a single quiet button, the
  // provider choice (Google/Microsoft) happens inside the modal itself.
  const [showPersonalInboxModal, setShowPersonalInboxModal] = useState(false)
  // Live add-on price for the paywall modal (GET-8966). null = loading;
  // 'unavailable' = pricing lookup failed, connecting is blocked (never
  // connect without an unmistakable price on screen).
  const [personalInboxPrice, setPersonalInboxPrice] = useState<
    { price_formatted: string; interval: string } | 'unavailable' | null
  >(null)
  // personal-inbox-explicit-opt-in: the test arm says when the add-on starts
  // charging and keeps the provider buttons locked until the owner ticks an
  // unchecked "add this to my subscription" box. Decided when the modal opens,
  // and only for someone a connect would actually bill, so exposure is exactly
  // the people who could be surprised by the charge.
  const [personalInboxSubscription, setPersonalInboxSubscription] =
    useState<SubscriptionResponse | null>(null)
  const [addonOptInRequired, setAddonOptInRequired] = useState(false)
  const [addonOptedIn, setAddonOptedIn] = useState(false)
  const [personalEmailConnected, setPersonalEmailConnected] = useState(false)
  const [personalEmailError, setPersonalEmailError] = useState<string | null>(null)
  // Failure of the work-account connect button below. Deliberately its own slot
  // rather than one shared with the integration cards' scope connects: those
  // controls live in a different section entirely, and an error surfacing under
  // the wrong button is barely better than no error at all.
  const [workAccountConnectError, setWorkAccountConnectError] = useState<string | null>(null)
  // Disables the button for the life of one attempt. `scopeLoading` tracks the
  // STATUS fetch, not this, so without it the button stays live while a connect
  // is in flight — and the new copy explicitly invites a retry. Two overlapping
  // starts each mint an `oauth_state` cookie server-side, so whichever redirect
  // the browser commits to can carry the other's state and fail the callback.
  const [workAccountConnecting, setWorkAccountConnecting] = useState(false)
  const [connectedPersonalAccounts, setConnectedPersonalAccounts] = useState<ConnectedAccountSummary[]>([])
  // The IMAP credentials form (GET-17286). `email` is set for a reconnect,
  // where only the password is re-entered.
  const [imapModal, setImapModal] = useState<
    { reconnect: { accountId: number; email: string } | null } | null
  >(null)
  const [showGranolaModal, setShowGranolaModal] = useState(false)
  const [fathomStatus, setFathomStatus] = useState<FathomStatusResponse | null>(null)
  const [fathomLoading, setFathomLoading] = useState(false)
  const [showFathomModal, setShowFathomModal] = useState(false)
  const [otterStatus, setOtterStatus] = useState<OtterStatusResponse | null>(null)
  const [otterLoading, setOtterLoading] = useState(false)
  const [showOtterModal, setShowOtterModal] = useState(false)
  const [wisprStatus, setWisprStatus] = useState<WisprStatusResponse | null>(null)
  const [wisprLoading, setWisprLoading] = useState(false)
  const [showWisprModal, setShowWisprModal] = useState(false)
  const [clickupStatus, setClickupStatus] = useState<ClickUpStatusResponse | null>(null)
  const [clickupLoading, setClickupLoading] = useState(false)
  const [showClickupModal, setShowClickupModal] = useState(false)
  const [asanaStatus, setAsanaStatus] = useState<AsanaStatusResponse | null>(null)
  const [asanaLoading, setAsanaLoading] = useState(false)
  const [showAsanaModal, setShowAsanaModal] = useState(false)
  const [notionStatus, setNotionStatus] = useState<NotionStatusResponse | null>(null)
  const [notionLoading, setNotionLoading] = useState(false)
  const [showNotionModal, setShowNotionModal] = useState(false)
  const [hubspotStatus, setHubspotStatus] = useState<HubSpotStatusResponse | null>(null)
  const [hubspotLoading, setHubspotLoading] = useState(false)
  const [showHubspotModal, setShowHubspotModal] = useState(false)
  const [attioStatus, setAttioStatus] = useState<AttioStatusResponse | null>(null)
  const [attioLoading, setAttioLoading] = useState(false)
  const [showAttioModal, setShowAttioModal] = useState(false)
  const [zohoStatus, setZohoStatus] = useState<IntegrationStatusResponse | null>(null)
  const [zohoLoading, setZohoLoading] = useState(false)
  const [showZohoModal, setShowZohoModal] = useState(false)
  const [salesforceStatus, setSalesforceStatus] = useState<SalesforceStatusResponse | null>(null)
  const [salesforceLoading, setSalesforceLoading] = useState(false)
  const [showSalesforceModal, setShowSalesforceModal] = useState(false)
  const [honeybookStatus, setHoneybookStatus] = useState<HoneyBookStatusResponse | null>(null)
  const [honeybookLoading, setHoneybookLoading] = useState(false)
  const [showHoneybookModal, setShowHoneybookModal] = useState(false)
  const [scopeStatus, setScopeStatus] = useState<ScopeStatusResponse | null>(null)
  const [scopeLoading, setScopeLoading] = useState(false)
  // Google scope clusters the user just tried to grant but came back without
  // (e.g. left the Drive box unchecked, so "docs" is only partially granted).
  const [incompleteScopeClusters, setIncompleteScopeClusters] = useState<string[]>([])
  // Result banner for the mobile OAuth redirect flow (GET-8963): set when we
  // land back on /integrations after a full-page-redirect connect.
  const [integrationReturnNotice, setIntegrationReturnNotice] = useState<
    { ok: boolean; label: string; reason?: string | undefined } | null
  >(null)
  const [assistantCharacter, setAssistantCharacter] = useState<AssistantCharacter | null>(null)
  const [assistantLoading, setAssistantLoading] = useState(false)
  const [availableCharacters, setAvailableCharacters] = useState<AssistantCharacter[]>([])
  const [showCharacterModal, setShowCharacterModal] = useState(false)
  const [changingCharacter, setChangingCharacter] = useState(false)
  const [showCustomCharacterForm, setShowCustomCharacterForm] = useState(false)
  const [deletingCustomCharacter, setDeletingCustomCharacter] = useState(false)

  // Multi-instance account tracking for the OAuth integrations — each can have
  // more than one connected account (e.g. two Notion workspaces). Drives the
  // per-tile "accounts" modal and the label field on the connect modals.
  const granolaAccounts = useIntegrationInstances(granolaService)
  const fathomAccounts = useIntegrationInstances(fathomService)
  const otterAccounts = useIntegrationInstances(otterService)
  const wisprAccounts = useIntegrationInstances(wisprService)
  const clickupAccounts = useIntegrationInstances(clickupService)
  const asanaAccounts = useIntegrationInstances(asanaService)
  const notionAccounts = useIntegrationInstances(notionService)
  const hubspotAccounts = useIntegrationInstances(hubspotService)
  const attioAccounts = useIntegrationInstances(attioService)
  const salesforceAccounts = useIntegrationInstances(salesforceService)
  const honeybookAccounts = useIntegrationInstances(honeybookService)
  // Zoho keeps its bespoke connect flow (the user provisions their own MCP
  // server) but its rows live in user_integrations, so the shared hook drives
  // its accounts modal too.
  const zohoAccounts = useIntegrationInstances(zohoService)

  const isGoogleUser = user?.calendar_provider !== 'microsoft'
  // Single source of truth for both providers: backend /scope-status. For
  // Google it checks granted OAuth scopes via tokeninfo; for Microsoft it
  // runs a refresh-token liveness probe (see microsoft_scope_service.py).
  // The "Connected" pill now reflects what the agent actually sees on its
  // next API call, not just "the user once finished signup" — so revoked
  // consent or an invalidated refresh token surfaces here.
  const mailCalendarConnected = scopeStatus?.mail_calendar ?? false

  // After an incremental-scope grant, CallbackPage forwards us here with
  // { scopeUpgrade, requestedClusters } in router state. If a requested cluster
  // is still ungranted, the user approved only part of Google's consent screen.
  const location = useLocation()
  const scopeUpgradeNavState = location.state as
    | { scopeUpgrade?: boolean; requestedClusters?: string | null }
    | null

  // Deep link: ?connect=<type> auto-opens the connection modal for that integration
  const [pendingConnect, setPendingConnect] = useState<string | null>(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get('connect')
  })
  // Deep link: ?add_inbox=1 — the assistant's "add another inbox" hand-off,
  // which lands here after sending a signed-out user through their provider
  // (GET-9017). Captured at mount like its siblings rather than read inside the
  // effect: the effect has to wait for the PostHog flag to resolve, and by then
  // the shared cleanup below may already have stripped the query string.
  const [pendingAddInbox, setPendingAddInbox] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get(CONNECT_INBOX_PARAM) === CONNECT_INBOX_PARAM_VALUE
  })
  // Deep link: ?create_webhook=1&name=...&description=... auto-opens the
  // Create Webhook modal with the fields pre-filled. Unlike pendingConnect,
  // this doesn't depend on integration statuses, so it can flow straight
  // through as a prop without waiting on statusesLoaded.
  const [pendingWebhook] = useState<{ name: string; description: string } | null>(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('create_webhook') !== '1') return null
    return {
      name: params.get('name') ?? '',
      description: params.get('description') ?? '',
    }
  })
  // Deep link: ?connect_app=<slug>&connect_app_name=<name> — the link
  // `connect_integration` hands the user for a catalog app. See
  // `shouldShowPipedreamCatalog` for why the browse flag must not gate it.
  // The name is what the catalog can actually search on; the slug only
  // identifies the request, so it is the fallback rather than the query.
  const [pendingAppConnectQuery] = useState<string | null>(() => {
    const params = new URLSearchParams(window.location.search)
    const slug = params.get('connect_app')
    if (!slug) return null
    return params.get('connect_app_name') || slug
  })
  // Deep link: ?connect=google_drive — Drive is the one integration with two
  // routes of different power (read-only via the Google scope, read & write via
  // Pipedream), so the link opens the chooser rather than picking one. Captured
  // at mount like its siblings: `pendingConnect` is cleared inside the effect
  // below, so it cannot be read at render time.
  const [pendingDriveConnect] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get('connect') === 'google_drive'
  })
  // Deep link: ?referral_link=1 (the referral promo page's "Get your referral
  // link" CTA) pops the referral spotlight so the link is impossible to miss.
  // The sessionStorage intent covers logged-out arrivals, whose query string
  // is lost on the login redirect.
  const [showReferralSpotlight, setShowReferralSpotlight] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get('referral_link') === '1' || hasReferralSpotlightIntent()
  })
  const [statusesLoaded, setStatusesLoaded] = useState(false)

  // Clean up the deep-link params from the URL so refreshing doesn't re-trigger
  useEffect(() => {
    if (
      pendingConnect
      || pendingWebhook
      || pendingAppConnectQuery
      || showReferralSpotlight
      || pendingAddInbox
    ) {
      window.history.replaceState({}, '', window.location.pathname)
    }
    if (showReferralSpotlight) {
      clearReferralSpotlightIntent()
    }
  }, [])

  // Track page view on mount. Feature flags above stay reactive to PostHog
  // identity changes and subsequent reloads for the lifetime of this page.
  useEffect(() => {
    trackEvent(OnboardingEvents.INTEGRATIONS_PAGE_VIEWED)
  }, [])

  // Load the user's connected personal inboxes once the flag resolves (and
  // again right after a successful connect redirect).
  useEffect(() => {
    if (!personalEmailFeatureEnabled) return
    authService.getConnectedEmailAccounts()
      .then(setConnectedPersonalAccounts)
      .catch((error) => {
        console.error('Failed to load connected email accounts:', error)
      })
  }, [personalEmailFeatureEnabled, personalEmailConnected])

  // Handle the connect-personal-inbox OAuth callback return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const status = params.get('connected_email')
    if (!status) return
    if (status === 'success') {
      setPersonalEmailConnected(true)
    } else if (status === 'primary_account') {
      setPersonalEmailError(
        "That's the account already connected to Catch — pick a different inbox.",
      )
    } else if (status === 'limit') {
      setPersonalEmailError(
        'An inbox is already connected — disconnect it to switch.',
      )
    } else if (status === 'billing_error') {
      setPersonalEmailError(
        "We couldn't set up the add-on billing, so nothing was connected and nothing was charged. Please try again.",
      )
    } else if (status === 'missing_permissions') {
      setPersonalEmailError(
        'That sign-in left out some of the access Catch needs — connecting an inbox grants reading, organizing, sending, and calendar together. Try again and approve everything requested.',
      )
    } else {
      setPersonalEmailError(
        "Connecting didn't finish — no changes were made. Feel free to try again.",
      )
    }
    window.history.replaceState({}, '', window.location.pathname)
  }, [])

  // Handle Granola OAuth callback URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const granolaConnected = params.get('granola_connected')
    const granolaError = params.get('granola_error')

    if (granolaConnected === 'true' || granolaError) {
      // Clean up URL
      const newUrl = window.location.pathname
      window.history.replaceState({}, '', newUrl)

      if (granolaConnected === 'true') {
        // Refresh Granola status
        loadGranolaStatus()
        trackEvent(OnboardingEvents.GRANOLA_CONNECTED)
      } else if (granolaError) {
        console.error('Granola OAuth error:', granolaError)
        trackEvent(OnboardingEvents.GRANOLA_CONNECTION_FAILED, { error: granolaError })
      }
    }
  }, [])

  // Handle the mobile full-page-redirect OAuth return (GET-8963). Desktop uses
  // a popup and never lands here; mobile connects via a top-level redirect and
  // the backend sends us back to /integrations?integration_connected=<type>
  // (or ?integration_error=<type>&integration_error_reason=...). All statuses
  // reload on this fresh mount, so the tile already reflects the new
  // connection — we just surface a result banner, fire analytics, and clean the
  // params off the URL so a refresh doesn't re-trigger.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const connected = params.get('integration_connected')
    const failed = params.get('integration_error')
    if (!connected && !failed) return

    window.history.replaceState({}, '', window.location.pathname)

    if (connected) {
      setIntegrationReturnNotice({ ok: true, label: integrationReturnLabel(connected) })
      trackEvent(OnboardingEvents.INTEGRATION_CONNECTED_VIA_REDIRECT, {
        integration: connected,
      })
    } else if (failed) {
      const reason = params.get('integration_error_reason') ?? undefined
      setIntegrationReturnNotice({
        ok: false,
        label: integrationReturnLabel(failed),
        reason,
      })
      trackEvent(OnboardingEvents.INTEGRATION_CONNECT_FAILED_VIA_REDIRECT, {
        integration: failed,
        error: reason,
      })
    }
  }, [])


  // Load integration statuses when user is available
  useEffect(() => {
    if (user) {
      const loaders: Promise<void>[] = [
        loadWhatsAppStatus(),
        loadSlackStatus(),
        loadSlackPersonalStatus(),
        loadTeamsAccessStatus(),
        loadZoomStatus(),
        loadGranolaStatus(),
        loadFathomStatus(),
        loadOtterStatus(),
        loadWisprStatus(),
        loadAssistantCharacter(),
        loadAvailableCharacters(),
      ]
      if (smsFeatureEnabled) loaders.push(loadSmsStatus())
      loaders.push(loadImessageStatus())
      loaders.push(loadClickupStatus())
      loaders.push(loadAsanaStatus())
      loaders.push(loadNotionStatus())
      loaders.push(loadHubspotStatus())
      loaders.push(loadAttioStatus())
      loaders.push(loadSalesforceStatus())
      loaders.push(loadHoneybookStatus())
      loaders.push(loadZohoStatus())
      // Scope status is provider-agnostic now — the backend dispatches by
      // user.calendar_provider and Microsoft gets a refresh-token liveness
      // probe (see microsoft_scope_service.py). Without this loader running
      // for Microsoft users, scopeStatus stays null and the Mail & Calendar
      // pill below permanently renders 'Partial'.
      loaders.push(loadScopeStatus())

      void Promise.all(loaders).then(() => setStatusesLoaded(true))
    }
  }, [user, smsFeatureEnabled])

  // Clusters to warn about, from two sources that answer different questions.
  // The return leg of a connect the user just performed knows which clusters
  // they asked for and didn't get, including ones granted nothing at all. The
  // server's `incomplete_clusters` covers the rest of the time: a cluster left
  // half-granted in an earlier session, which was invisible here before
  // GET-11570 — and worse, the pill below reads 'Connected' for it, because
  // `mail_calendar` is true off any Gmail plus any Calendar scope.
  //
  // The return leg wins when it has something to say; otherwise the server
  // does, including when it says nothing, so a repaired grant clears the
  // banner instead of leaving it up until reload.
  useEffect(() => {
    if (!statusesLoaded || !scopeStatus) return
    const fromReturnLeg = scopeUpgradeNavState?.scopeUpgrade
      ? missingScopeClusters(
        scopeStatus,
        parseScopeClusters(scopeUpgradeNavState.requestedClusters),
      )
      : []
    setIncompleteScopeClusters(
      fromReturnLeg.length > 0
        ? fromReturnLeg
        : Object.keys(scopeStatus.incomplete_clusters ?? {}),
    )
    if (fromReturnLeg.length > 0) {
      trackEvent(OnboardingWizardEvents.PERMISSIONS_INCOMPLETE, {
        clusters: fromReturnLeg,
        source: 'integrations',
      })
    }
  }, [scopeUpgradeNavState, statusesLoaded, scopeStatus])

  const incompleteScopeLabel = formatScopeClusterList(incompleteScopeClusters)
  const onlyOneIncompleteScope = incompleteScopeClusters.length === 1

  // Deep link: once all integration statuses are loaded, auto-open the requested modal.
  useEffect(() => {
    if (!pendingConnect || !statusesLoaded) return
    setPendingConnect(null)

    switch (pendingConnect) {
      case 'whatsapp': if (!whatsappStatus?.connected) setShowWhatsAppModal(true); break
      case 'slack': if (!slackStatus?.connected) setShowSlackModal(true); break
      case 'slack_personal': if (!slackPersonalStatus?.connected) setShowSlackPersonalModal(true); break
      case 'sms': if (smsFeatureEnabled && !smsStatus?.connected) setShowSmsModal(true); break
      case 'imessage': if (!imessageStatus?.connected) setShowImessageModal(true); break
      case 'zoom': if (zoomAvailable && !zoomStatus?.connected) setShowZoomModal(true); break
      case 'google_docs': if (isGoogleUser && !scopeStatus?.docs) void handleConnectScope('docs'); break
      // Drive alone is NOT handled here: it has two routes of different power,
      // and granting the read-only scope on the user's behalf is exactly what
      // the chooser exists to stop. The section opens that chooser instead,
      // via `pendingDriveAccess` below.
      case 'google_drive': break
      case 'google_meet': if (isGoogleUser && !scopeStatus?.meet) void handleConnectScope('meet'); break
      case 'granola': if (!granolaStatus?.connected) setShowGranolaModal(true); break
      case 'fathom': if (!fathomStatus?.connected) setShowFathomModal(true); break
      case 'otter': if (!otterStatus?.connected) setShowOtterModal(true); break
      case 'wispr': if (!wisprStatus?.connected) setShowWisprModal(true); break
      case 'clickup': if (!clickupStatus?.connected) setShowClickupModal(true); break
      case 'asana': if (!asanaStatus?.connected) setShowAsanaModal(true); break
      case 'notion': if (!notionStatus?.connected) setShowNotionModal(true); break
      case 'hubspot': if (!hubspotStatus?.connected) setShowHubspotModal(true); break
      case 'attio': if (!attioStatus?.connected) setShowAttioModal(true); break
      case 'salesforce': if (!salesforceStatus?.connected) setShowSalesforceModal(true); break
      case 'honeybook': if (!honeybookStatus?.connected) setShowHoneybookModal(true); break
      case 'zoho': if (!zohoStatus?.connected) setShowZohoModal(true); break
    }
  }, [
    pendingConnect, statusesLoaded,
    whatsappStatus, slackStatus, smsStatus, imessageStatus,
    zoomStatus, zoomAvailable, clickupStatus, asanaStatus, notionStatus, hubspotStatus, attioStatus, salesforceStatus, honeybookStatus, zohoStatus, scopeStatus, granolaStatus, fathomStatus, otterStatus,
    smsFeatureEnabled,
  ])

  const loadWhatsAppStatus = async () => {
    if (!user) return

    setWhatsappLoading(true)
    try {
      const status = await whatsappService.getStatus()
      setWhatsappStatus(status)
    } catch (error) {
      console.error('Failed to load WhatsApp status:', error)
      setWhatsappStatus({ connected: false, verified: false })
    } finally {
      setWhatsappLoading(false)
    }
  }

  const handleConnectWhatsApp = () => {
    trackEvent(OnboardingEvents.WHATSAPP_CONNECT_CLICKED)
    setShowWhatsAppModal(true)
  }

  const handleDisconnectWhatsApp = async () => {
    setWhatsappLoading(true)
    try {
      await whatsappService.disconnect()
      trackEvent(OnboardingEvents.WHATSAPP_DISCONNECTED)
      await loadWhatsAppStatus() // Refresh status
    } catch (error) {
      console.error('Failed to disconnect WhatsApp:', error)
    } finally {
      setWhatsappLoading(false)
    }
  }

  const handleWhatsAppSuccess = async () => {
    await loadWhatsAppStatus() // Refresh status after successful connection
  }

  const loadSlackStatus = async () => {
    if (!user) return

    setSlackLoading(true)
    try {
      const status = await slackService.getStatus()
      setSlackStatus(status)
    } catch (error) {
      console.error('Failed to load Slack status:', error)
      setSlackStatus({ connected: false, slack_user_id: null, workspace_name: null })
    } finally {
      setSlackLoading(false)
    }
  }

  const handleConnectSlack = () => {
    trackEvent(OnboardingEvents.SLACK_CONNECT_CLICKED)
    setShowSlackModal(true)
  }

  const handleDisconnectSlack = async () => {
    setSlackLoading(true)
    try {
      await slackService.disconnect()
      trackEvent(OnboardingEvents.SLACK_DISCONNECTED)
      await loadSlackStatus() // Refresh status
    } catch (error) {
      console.error('Failed to disconnect Slack:', error)
    } finally {
      setSlackLoading(false)
    }
  }

  const loadSlackPersonalStatus = async () => {
    setSlackPersonalLoading(true)
    try {
      setSlackPersonalStatus(await slackPersonalService.getStatus())
    } catch (error) {
      console.error('Failed to load Slack personal status:', error)
      setSlackPersonalStatus({ connected: false, workspace_name: null, access_mode: null, channel_count: 0 })
    } finally {
      setSlackPersonalLoading(false)
    }
  }

  const handleDisconnectSlackPersonal = async () => {
    setSlackPersonalLoading(true)
    try {
      await slackPersonalService.disconnect()
      await loadSlackPersonalStatus()
    } catch (error) {
      console.error('Failed to disconnect Slack personal access:', error)
    } finally {
      setSlackPersonalLoading(false)
    }
  }

  const slackPersonalDetail = (status: SlackPersonalStatusResponse | null): string | undefined => {
    if (!status?.connected) return undefined
    const n = status.channel_count
    if (status.access_mode === 'opt_out') return n === 0 ? 'Reads everything' : `All except ${n}`
    return n === 1 ? '1 conversation' : `${n} conversations`
  }

  // The permission on the Pipedream Teams connection (GET-14326). Loaded
  // beside the other statuses; a failed read leaves the tile's detail blank
  // rather than wrong.
  const loadTeamsAccessStatus = async () => {
    try {
      setTeamsAccessStatus(await teamsAccessService.getStatus())
    } catch (error) {
      console.error('Failed to load Teams access status:', error)
      setTeamsAccessStatus(null)
    }
  }

  const teamsAccessDetail = (status: TeamsAccessStatusResponse | null): string | undefined => {
    if (!status?.connected) return undefined
    const n = status.conversation_count
    if (status.access_mode === 'opt_out') return n === 0 ? 'Reads everything' : `Read only · all except ${n}`
    return n === 0 ? 'Read only · nothing yet' : n === 1 ? 'Read only · 1 conversation' : `Read only · ${n} conversations`
  }

  const handleSlackSuccess = async () => {
    await loadSlackStatus() // Refresh status after successful connection
  }

  const loadZoomStatus = async () => {
    if (!user) return

    setZoomLoading(true)
    try {
      const status = await zoomService.getStatus()
      setZoomStatus(status)
      setZoomAvailable(true)
    } catch (error: any) {
      if (error?.response?.status === 403) {
        setZoomAvailable(false)
      } else {
        console.error('Failed to load Zoom status:', error)
        setZoomStatus({ connected: false, zoom_user_id: null, email: null })
        setZoomAvailable(true)
      }
    } finally {
      setZoomLoading(false)
    }
  }

  const handleConnectZoom = () => {
    trackEvent(OnboardingEvents.ZOOM_CONNECT_CLICKED)
    setShowZoomModal(true)
  }

  const handleDisconnectZoom = async () => {
    setZoomLoading(true)
    try {
      await zoomService.disconnect()
      trackEvent(OnboardingEvents.ZOOM_DISCONNECTED)
      await loadZoomStatus() // Refresh status
    } catch (error) {
      console.error('Failed to disconnect Zoom:', error)
    } finally {
      setZoomLoading(false)
    }
  }

  const handleZoomSuccess = async () => {
    await loadZoomStatus() // Refresh status after successful connection
  }

  const loadSmsStatus = async () => {
    if (!user) return

    setSmsLoading(true)
    try {
      const status = await smsService.getStatus()
      setSmsStatus(status)
    } catch (error) {
      console.error('Failed to load SMS status:', error)
      setSmsStatus({ connected: false, verified: false })
    } finally {
      setSmsLoading(false)
    }
  }

  const handleConnectSms = () => {
    trackEvent(OnboardingEvents.SMS_CONNECT_CLICKED)
    setShowSmsModal(true)
  }

  const handleDisconnectSms = async () => {
    setSmsLoading(true)
    try {
      await smsService.disconnect()
      trackEvent(OnboardingEvents.SMS_DISCONNECTED)
      await loadSmsStatus() // Refresh status
    } catch (error) {
      console.error('Failed to disconnect SMS:', error)
    } finally {
      setSmsLoading(false)
    }
  }

  const handleSmsSuccess = async () => {
    await loadSmsStatus() // Refresh status after successful connection
  }

  const loadImessageStatus = async () => {
    if (!user) return

    setImessageLoading(true)
    try {
      const status = await imessageService.getStatus()
      setImessageStatus(status)
    } catch (error) {
      console.error('Failed to load iMessage status:', error)
      setImessageStatus({ connected: false, verified: false })
    } finally {
      setImessageLoading(false)
    }
  }

  const handleConnectImessage = () => {
    trackEvent(OnboardingEvents.IMESSAGE_CONNECT_CLICKED)
    setShowImessageModal(true)
  }

  const handleDisconnectImessage = async () => {
    setImessageLoading(true)
    try {
      await imessageService.disconnect()
      trackEvent(OnboardingEvents.IMESSAGE_DISCONNECTED)
      await loadImessageStatus()
    } catch (error) {
      console.error('Failed to disconnect iMessage:', error)
    } finally {
      setImessageLoading(false)
    }
  }

  const handleRemoveImessageHandle = async (identifier: string) => {
    setImessageLoading(true)
    try {
      await imessageService.disconnect(identifier)
      trackEvent(OnboardingEvents.IMESSAGE_DISCONNECTED, { identifier })
      await loadImessageStatus()
    } catch (error) {
      console.error('Failed to remove iMessage handle:', error)
    } finally {
      setImessageLoading(false)
    }
  }

  const handleImessageSuccess = async () => {
    await loadImessageStatus()
  }

  const loadGranolaStatus = async () => {
    if (!user) return

    setGranolaLoading(true)
    try {
      const status = await granolaService.getStatus()
      setGranolaStatus(status)
    } catch (error) {
      console.error('Failed to load Granola status:', error)
      setGranolaStatus({ connected: false, external_user_id: null, granola_user_id: null, connected_at: null })
    } finally {
      setGranolaLoading(false)
    }
  }

  const loadScopeStatus = async () => {
    if (!user) return

    setScopeLoading(true)
    try {
      const status = await scopeService.getStatus()
      setScopeStatus(status)
    } catch (error) {
      console.error('Failed to load scope status:', error)
    } finally {
      setScopeLoading(false)
    }
  }

  const trackConnectFailure = (cluster: string, error: unknown): void => {
    trackEvent(OnboardingWizardEvents.SCOPE_CONNECT_FAILED, {
      cluster,
      status: getApiErrorStatus(error) ?? null,
    })
  }

  const reportWorkAccountConnectFailure = (error: unknown): void => {
    setWorkAccountConnectError(
      getUserFacingApiErrorMessage(
        error,
        isGoogleUser ? MAIL_ACCESS_START_FAILED : RECONNECT_START_FAILED,
      ),
    )
    setWorkAccountConnecting(false)
  }

  // Consent is always forced: every connect here targets a scope the user
  // doesn't yet have, and include_granted_scopes=true would otherwise let Google
  // silently re-issue a prior partial grant without re-showing the checkboxes —
  // stranding anyone who left a box unchecked. Forcing consent guarantees the
  // granular screen appears so they can approve the full set.
  //
  // `report` lets a caller whose control has an error slot show the failure.
  // Without one this only reaches console.error, which is not a user-facing
  // surface — the button just looks dead.
  const handleConnectScope = async (
    cluster: ScopeCluster,
    report?: (error: unknown) => void,
  ) => {
    trackEvent(OnboardingWizardEvents.SCOPE_CONNECT_CLICKED, { cluster, force_consent: true })
    try {
      await scopeService.requestScopes([cluster], '/integrations', true)
    } catch (error) {
      console.error('Failed to request scope:', error)
      // Emitted here, not in the caller's reporter: this is where the real
      // `cluster` is, so the event cannot disagree with what was requested, and
      // the four call sites with no error slot of their own still get counted.
      // Without an event the class is visible only as the absence of
      // SCOPE_CONNECTED — the inference that let the login page's equivalent
      // outage run 15 hours (GET-12701 / GET-12886).
      trackConnectFailure(cluster, error)
      report?.(error)
    }
  }

  const handleRetryIncompleteScopes = async () => {
    if (incompleteScopeClusters.length === 0) return
    trackEvent(OnboardingWizardEvents.SCOPE_CONNECT_CLICKED, {
      cluster: incompleteScopeClusters.join(','),
      force_consent: true,
      retry: true,
    })
    try {
      await scopeService.requestScopes(incompleteScopeClusters, '/integrations', true)
    } catch (error) {
      console.error('Failed to re-request scopes:', error)
      trackConnectFailure(incompleteScopeClusters.join(','), error)
    }
  }

  // The work-account connect, shared by the Mail & Calendar card and the
  // simple-onboarding card: one attempt at a time (see workAccountConnecting),
  // the Google incremental grant or the Microsoft full re-auth by provider.
  const handleConnectWorkAccount = (): void => {
    setWorkAccountConnectError(null)
    setWorkAccountConnecting(true)
    void (isGoogleUser
      ? handleConnectScope('mail_calendar', reportWorkAccountConnectFailure)
      : handleReconnectMicrosoft())
  }

  // Microsoft has no incremental consent — the only remediation for a missing
  // scope is a full re-auth, which re-runs the all-or-nothing consent screen.
  // Sends the user through the standard Microsoft login flow.
  const handleReconnectMicrosoft = async () => {
    trackEvent(OnboardingWizardEvents.SCOPE_CONNECT_CLICKED, {
      cluster: 'microsoft_reauth',
    })
    try {
      const { auth_url } = await authService.getMicrosoftLoginUrl()
      // A 200 whose body lacks auth_url would otherwise coerce to the string
      // "undefined" and navigate to /settings/undefined, with the catch never
      // running — a blank route and no explanation.
      if (!auth_url) throw new Error('Microsoft login URL missing auth_url')
      leaveForSignIn('microsoft', auth_url, { reconnect: true })
    } catch (error) {
      console.error('Failed to start Microsoft re-auth:', error)
      trackConnectFailure('microsoft_reauth', error)
      reportWorkAccountConnectFailure(error)
    }
  }

  // Connect a personal Gmail or Outlook account (read + draft only). Redirects
  // the browser into the provider's consent; the backend persists the account
  // on callback and sends the user back to /settings?connected_email=success.
  // Opens the paywall modal: instruments the click and loads the live add-on
  // price. Connecting stays blocked until a price is on screen (GET-8966).
  const handleOpenPersonalInboxModal = async () => {
    trackEvent(OnboardingEvents.PERSONAL_INBOX_CONNECT_CLICKED)
    setShowPersonalInboxModal(true)
    setPersonalInboxPrice(null)
    setAddonOptInRequired(false)
    setAddonOptedIn(false)
    const [price, subscription] = await Promise.all([
      authService.getPersonalInboxAddonPricing().catch(() => 'unavailable' as const),
      // An unreadable subscription is treated as unbilled: the owner keeps
      // the control modal and stays out of the experiment.
      billingService.getSubscription().catch(() => null),
    ])
    setPersonalInboxSubscription(subscription)
    if (isBilledSubscription(subscription)) {
      // The add-inbox deep link lands here straight after a sign-in, when the
      // loaded flags can still be the anonymous visitor's; reading those would
      // enroll the owner in two arms (GET-16313). The wait fails open.
      await flagsSettledForIdentifiedUser()
      // Reading the variant here is the exposure.
      setAddonOptInRequired(requiresExplicitAddonOptIn(
        getFeatureFlagVariantSync(FeatureFlags.PERSONAL_INBOX_EXPLICIT_OPT_IN),
      ))
    }
    setPersonalInboxPrice(price)
  }
  const personalInboxConnectLocked =
    connectingPersonalEmail
    || personalInboxPrice === null
    || personalInboxPrice === 'unavailable'
    || (addonOptInRequired && !addonOptedIn)

  // Open the add-on modal for the assistant's "add another inbox" link, which
  // lands here rather than making the user find the card (GET-9017). The modal
  // and not the provider consent behind it: each inbox is billed as its own
  // add-on, so a link may deep-link to the price, never past it.
  //
  // Waits for the flag to resolve rather than firing at mount — unresolved
  // reads as "off", which would swallow the hand-off — and clears the intent as
  // it opens, so a dismissed modal is not reopened underneath the user.
  useEffect(() => {
    if (!pendingAddInbox || !personalEmailFeatureEnabled) return
    setPendingAddInbox(false)
    void handleOpenPersonalInboxModal()
  }, [pendingAddInbox, personalEmailFeatureEnabled])

  const handleConnectPersonalEmail = async (provider: 'google' | 'microsoft') => {
    setConnectingPersonalEmail(true)
    // A stale banner must not be left describing the attempt just started.
    setPersonalEmailError(null)
    try {
      const authUrl = provider === 'microsoft'
        ? await authService.getConnectPersonalEmailUrlMicrosoft()
        : await authService.getConnectPersonalEmailUrl()
      window.location.href = authUrl
    } catch (error) {
      // Same dead-button class as the work-account button one box above, and
      // the slot to report it in was already declared and already on screen —
      // it just had nothing writing to it (GET-12886).
      console.error('Failed to start personal-email connect:', error)
      setPersonalEmailError(
        getUserFacingApiErrorMessage(error, PERSONAL_INBOX_START_FAILED),
      )
      setConnectingPersonalEmail(false)
    }
  }

  const handleDisconnectPersonalEmail = async (accountId: number) => {
    try {
      await authService.disconnectConnectedEmailAccount(accountId)
      setConnectedPersonalAccounts((accounts) =>
        accounts.filter((account) => account.id !== accountId))
      setPersonalEmailConnected(false)
      setPersonalEmailError(null)
    } catch (error) {
      console.error('Failed to disconnect personal email:', error)
      setPersonalEmailError("Couldn't disconnect just now — give it another try.")
    }
  }

  const handleImapConnected = () => {
    setImapModal(null)
    setPersonalEmailError(null)
    setPersonalEmailConnected(true)
    authService.getConnectedEmailAccounts()
      .then(setConnectedPersonalAccounts)
      .catch((error) => {
        console.error('Failed to load connected email accounts:', error)
      })
  }

  const handleConnectGranola = () => {
    trackEvent(OnboardingEvents.GRANOLA_CONNECT_CLICKED)
    setShowGranolaModal(true)
  }

  const handleDisconnectGranola = async () => {
    setGranolaLoading(true)
    try {
      await granolaService.disconnect()
      trackEvent(OnboardingEvents.GRANOLA_DISCONNECTED)
      await loadGranolaStatus() // Refresh status
    } catch (error) {
      console.error('Failed to disconnect Granola:', error)
    } finally {
      setGranolaLoading(false)
    }
  }

  const handleGranolaSuccess = async () => {
    await loadGranolaStatus() // Refresh status after successful connection
    await granolaAccounts.reload()
  }

  const loadFathomStatus = async () => {
    if (!user) return

    setFathomLoading(true)
    try {
      const status = await fathomService.getStatus()
      setFathomStatus(status)
    } catch (error) {
      console.error('Failed to load Fathom status:', error)
      setFathomStatus({ connected: false, external_user_id: null, connected_at: null })
    } finally {
      setFathomLoading(false)
    }
  }

  const handleConnectFathom = () => {
    trackEvent(OnboardingEvents.FATHOM_CONNECT_CLICKED)
    setShowFathomModal(true)
  }

  const handleDisconnectFathom = async () => {
    setFathomLoading(true)
    try {
      await fathomService.disconnect()
      trackEvent(OnboardingEvents.FATHOM_DISCONNECTED)
      await loadFathomStatus()
    } catch (error) {
      console.error('Failed to disconnect Fathom:', error)
    } finally {
      setFathomLoading(false)
    }
  }

  const handleFathomSuccess = async () => {
    await loadFathomStatus()
    await fathomAccounts.reload()
  }

  const loadOtterStatus = async () => {
    if (!user) return

    setOtterLoading(true)
    try {
      const status = await otterService.getStatus()
      setOtterStatus(status)
    } catch (error) {
      console.error('Failed to load Otter status:', error)
      setOtterStatus({ connected: false, external_user_id: null, connected_at: null })
    } finally {
      setOtterLoading(false)
    }
  }

  const handleConnectOtter = () => {
    trackEvent(OnboardingEvents.OTTER_CONNECT_CLICKED)
    setShowOtterModal(true)
  }

  const handleDisconnectOtter = async () => {
    setOtterLoading(true)
    try {
      await otterService.disconnect()
      trackEvent(OnboardingEvents.OTTER_DISCONNECTED)
      await loadOtterStatus()
    } catch (error) {
      console.error('Failed to disconnect Otter:', error)
    } finally {
      setOtterLoading(false)
    }
  }

  const handleOtterSuccess = async () => {
    await loadOtterStatus()
    await otterAccounts.reload()
  }

  const loadWisprStatus = async () => {
    if (!user) return

    setWisprLoading(true)
    try {
      const status = await wisprService.getStatus()
      setWisprStatus(status)
    } catch (error) {
      console.error('Failed to load Wispr Flow status:', error)
      setWisprStatus({ connected: false, external_user_id: null, connected_at: null })
    } finally {
      setWisprLoading(false)
    }
  }

  const handleConnectWispr = () => {
    trackEvent(OnboardingEvents.WISPR_CONNECT_CLICKED)
    setShowWisprModal(true)
  }

  const handleDisconnectWispr = async () => {
    setWisprLoading(true)
    try {
      await wisprService.disconnect()
      trackEvent(OnboardingEvents.WISPR_DISCONNECTED)
      await loadWisprStatus()
    } catch (error) {
      console.error('Failed to disconnect Wispr Flow:', error)
    } finally {
      setWisprLoading(false)
    }
  }

  const handleWisprSuccess = async () => {
    // Independent endpoints — run them concurrently so the post-OAuth refresh
    // costs one round trip of latency instead of two.
    await Promise.all([loadWisprStatus(), wisprAccounts.reload()])
  }

  const loadClickupStatus = async () => {
    if (!user) return

    setClickupLoading(true)
    try {
      const status = await clickupService.getStatus()
      setClickupStatus(status)
    } catch (error) {
      console.error('Failed to load ClickUp status:', error)
      setClickupStatus({ connected: false, external_user_id: null, connected_at: null })
    } finally {
      setClickupLoading(false)
    }
  }

  const handleConnectClickup = () => {
    trackEvent(OnboardingEvents.CLICKUP_CONNECT_CLICKED)
    setShowClickupModal(true)
  }

  const handleDisconnectClickup = async () => {
    setClickupLoading(true)
    try {
      await clickupService.disconnect()
      trackEvent(OnboardingEvents.CLICKUP_DISCONNECTED)
      await loadClickupStatus()
    } catch (error) {
      console.error('Failed to disconnect ClickUp:', error)
    } finally {
      setClickupLoading(false)
    }
  }

  const handleClickupSuccess = async () => {
    await loadClickupStatus()
    await clickupAccounts.reload()
  }

  const loadAsanaStatus = async () => {
    if (!user) return

    setAsanaLoading(true)
    try {
      const status = await asanaService.getStatus()
      setAsanaStatus(status)
    } catch (error) {
      console.error('Failed to load Asana status:', error)
      setAsanaStatus({ connected: false, external_user_id: null, connected_at: null })
    } finally {
      setAsanaLoading(false)
    }
  }

  const handleConnectAsana = () => {
    trackEvent(OnboardingEvents.ASANA_CONNECT_CLICKED)
    setShowAsanaModal(true)
  }

  const handleDisconnectAsana = async () => {
    setAsanaLoading(true)
    try {
      await asanaService.disconnect()
      trackEvent(OnboardingEvents.ASANA_DISCONNECTED)
      await loadAsanaStatus()
    } catch (error) {
      console.error('Failed to disconnect Asana:', error)
    } finally {
      setAsanaLoading(false)
    }
  }

  const handleAsanaSuccess = async () => {
    await loadAsanaStatus()
    await asanaAccounts.reload()
  }

  const loadNotionStatus = async () => {
    if (!user) return

    setNotionLoading(true)
    try {
      const status = await notionService.getStatus()
      setNotionStatus(status)
    } catch (error) {
      console.error('Failed to load Notion status:', error)
      setNotionStatus({ connected: false, external_user_id: null, connected_at: null })
    } finally {
      setNotionLoading(false)
    }
  }

  const handleConnectNotion = () => {
    trackEvent(OnboardingEvents.NOTION_CONNECT_CLICKED)
    setShowNotionModal(true)
  }

  const handleDisconnectNotion = async () => {
    setNotionLoading(true)
    try {
      await notionService.disconnect()
      trackEvent(OnboardingEvents.NOTION_DISCONNECTED)
      await loadNotionStatus()
    } catch (error) {
      console.error('Failed to disconnect Notion:', error)
    } finally {
      setNotionLoading(false)
    }
  }

  const handleNotionSuccess = async () => {
    await loadNotionStatus()
    await notionAccounts.reload()
  }

  const loadHubspotStatus = async () => {
    if (!user) return

    setHubspotLoading(true)
    try {
      const status = await hubspotService.getStatus()
      setHubspotStatus(status)
    } catch (error) {
      console.error('Failed to load HubSpot status:', error)
      setHubspotStatus({ connected: false, external_user_id: null, connected_at: null })
    } finally {
      setHubspotLoading(false)
    }
  }

  const handleConnectHubspot = () => {
    trackEvent(OnboardingEvents.HUBSPOT_CONNECT_CLICKED)
    setShowHubspotModal(true)
  }

  const handleDisconnectHubspot = async () => {
    setHubspotLoading(true)
    try {
      await hubspotService.disconnect()
      trackEvent(OnboardingEvents.HUBSPOT_DISCONNECTED)
      await loadHubspotStatus()
    } catch (error) {
      console.error('Failed to disconnect HubSpot:', error)
    } finally {
      setHubspotLoading(false)
    }
  }

  const handleHubspotSuccess = async () => {
    await loadHubspotStatus()
    await hubspotAccounts.reload()
  }

  const loadSalesforceStatus = async () => {
    if (!user) return

    setSalesforceLoading(true)
    try {
      const status = await salesforceService.getStatus()
      setSalesforceStatus(status)
    } catch (error) {
      console.error('Failed to load Salesforce status:', error)
      setSalesforceStatus({ connected: false, external_user_id: null, connected_at: null })
    } finally {
      setSalesforceLoading(false)
    }
  }

  const handleConnectSalesforce = () => {
    trackEvent(OnboardingEvents.SALESFORCE_CONNECT_CLICKED)
    setShowSalesforceModal(true)
  }

  const handleDisconnectSalesforce = async () => {
    setSalesforceLoading(true)
    try {
      await salesforceService.disconnect()
      trackEvent(OnboardingEvents.SALESFORCE_DISCONNECTED)
      await loadSalesforceStatus()
    } catch (error) {
      console.error('Failed to disconnect Salesforce:', error)
    } finally {
      setSalesforceLoading(false)
    }
  }

  const handleSalesforceSuccess = async () => {
    await loadSalesforceStatus()
    await salesforceAccounts.reload()
  }

  const loadHoneybookStatus = async () => {
    if (!user) return

    setHoneybookLoading(true)
    try {
      const status = await honeybookService.getStatus()
      setHoneybookStatus(status)
    } catch (error) {
      console.error('Failed to load HoneyBook status:', error)
      setHoneybookStatus({ connected: false, external_user_id: null, connected_at: null })
    } finally {
      setHoneybookLoading(false)
    }
  }

  const handleConnectHoneybook = () => {
    trackEvent(OnboardingEvents.HONEYBOOK_CONNECT_CLICKED)
    setShowHoneybookModal(true)
  }

  const handleDisconnectHoneybook = async () => {
    setHoneybookLoading(true)
    try {
      await honeybookService.disconnect()
      trackEvent(OnboardingEvents.HONEYBOOK_DISCONNECTED)
      // Reload BOTH: the tile reads `status.connected || instances.length > 0`,
      // so refreshing status alone leaves the stale instance list showing the
      // integration as still connected.
      await Promise.all([loadHoneybookStatus(), honeybookAccounts.reload()])
    } catch (error) {
      console.error('Failed to disconnect HoneyBook:', error)
    } finally {
      setHoneybookLoading(false)
    }
  }

  const handleHoneybookSuccess = async () => {
    // Independent endpoints — run them concurrently so the post-OAuth refresh
    // costs one round trip instead of two.
    await Promise.all([loadHoneybookStatus(), honeybookAccounts.reload()])
  }

  const loadAttioStatus = async () => {
    if (!user) return

    setAttioLoading(true)
    try {
      const status = await attioService.getStatus()
      setAttioStatus(status)
    } catch (error) {
      console.error('Failed to load Attio status:', error)
      setAttioStatus({ connected: false, external_user_id: null, connected_at: null })
    } finally {
      setAttioLoading(false)
    }
  }

  const handleConnectAttio = () => {
    trackEvent(OnboardingEvents.ATTIO_CONNECT_CLICKED)
    setShowAttioModal(true)
  }

  const handleDisconnectAttio = async () => {
    setAttioLoading(true)
    try {
      await attioService.disconnect()
      trackEvent(OnboardingEvents.ATTIO_DISCONNECTED)
      await loadAttioStatus()
    } catch (error) {
      console.error('Failed to disconnect Attio:', error)
    } finally {
      setAttioLoading(false)
    }
  }

  const handleAttioSuccess = async () => {
    await loadAttioStatus()
    await attioAccounts.reload()
  }

  const loadZohoStatus = async () => {
    if (!user) return

    setZohoLoading(true)
    try {
      const status = await zohoService.getStatus()
      setZohoStatus(status)
    } catch (error) {
      console.error('Failed to load Zoho status:', error)
      setZohoStatus({ connected: false, external_user_id: null, connected_at: null })
    } finally {
      setZohoLoading(false)
    }
  }

  const handleConnectZoho = () => {
    trackEvent(OnboardingEvents.ZOHO_CONNECT_CLICKED)
    setShowZohoModal(true)
  }

  const handleDisconnectZoho = async () => {
    setZohoLoading(true)
    try {
      await zohoService.disconnect()
      trackEvent(OnboardingEvents.ZOHO_DISCONNECTED)
      await loadZohoStatus()
    } catch (error) {
      console.error('Failed to disconnect Zoho:', error)
    } finally {
      setZohoLoading(false)
    }
  }

  const handleZohoSuccess = async () => {
    await loadZohoStatus()
    await zohoAccounts.reload()
  }

  const loadAssistantCharacter = async () => {
    if (!user) return

    setAssistantLoading(true)
    try {
      const character = await authService.getAssistantCharacter()
      setAssistantCharacter(character)
    } catch (error) {
      console.error('Failed to load assistant character:', error)
      setAssistantCharacter(null)
    } finally {
      setAssistantLoading(false)
    }
  }

  const loadAvailableCharacters = async () => {
    try {
      const characters = await authService.listAssistantCharacters()
      setAvailableCharacters(characters)
    } catch (error) {
      console.error('Failed to load available characters:', error)
    }
  }

  const handleChangeCharacter = async (characterId: number) => {
    setChangingCharacter(true)
    try {
      const updatedCharacter = await authService.updateAssistantCharacter(characterId)
      setAssistantCharacter(updatedCharacter)
      trackEvent(OnboardingEvents.ASSISTANT_SELECTED, {
        character_id: characterId,
        character_name: updatedCharacter.name,
      })
      setShowCharacterModal(false)
    } catch (error) {
      console.error('Failed to update assistant character:', error)
    } finally {
      setChangingCharacter(false)
    }
  }

  const handleOpenCharacterModal = () => {
    trackEvent(OnboardingEvents.ASSISTANT_CHANGE_CLICKED)
    setShowCharacterModal(true)
  }

  const handleCloseCharacterModal = () => {
    trackEvent(OnboardingEvents.ASSISTANT_MODAL_CLOSED)
    setShowCharacterModal(false)
  }

  const handleCustomCharacterCreated = (character: AssistantCharacter) => {
    setShowCustomCharacterForm(false)
    setAssistantCharacter(character)
    // Refresh the available characters list
    setAvailableCharacters((prev) => {
      const withoutCustom = prev.filter((c) => !c.is_custom)
      return [...withoutCustom, character]
    })
    setShowCharacterModal(false)
    // Poll for image generation
    pollForCharacterImage(character.id)
  }

  /**
   * An upload or revert saved immediately, outside the form's submit. A revert
   * leaves the character with no picture while Replicate draws a new one, so
   * poll the way a persona edit does.
   */
  const handleCharacterPictureChanged = (character: AssistantCharacter) => {
    setAssistantCharacter((prev) => (prev?.id === character.id ? character : prev))
    setAvailableCharacters((prev) =>
      prev.map((c) => (c.id === character.id ? character : c)),
    )
    if (!character.picture_url) {
      pollForCharacterImage(character.id)
    }
  }

  const handleDeleteCustomCharacter = async () => {
    trackEvent(OnboardingEvents.ASSISTANT_CUSTOM_DELETE_CLICKED)
    if (!confirm('Are you sure you want to delete your custom character?')) return
    setDeletingCustomCharacter(true)
    try {
      await authService.deleteCustomCharacter()
      // Drop any saved builder draft for the deleted character so a stale
      // :edit:<id> key isn't left behind.
      clearCustomCharacterDraft(existingCustomCharacter?.id ?? null)
      trackEvent(OnboardingEvents.ASSISTANT_CUSTOM_DELETED)
      setAvailableCharacters((prev) => prev.filter((c) => !c.is_custom))
      // Refresh current character (will be reset to default by backend)
      await loadAssistantCharacter()
    } catch (error) {
      console.error('Failed to delete custom character:', error)
    } finally {
      setDeletingCustomCharacter(false)
    }
  }

  const pollForCharacterImage = (characterId: number) => {
    let attempts = 0
    const maxAttempts = 24 // 2 minutes at 5s intervals
    const interval = setInterval(async () => {
      attempts++
      try {
        const characters = await authService.listAssistantCharacters()
        const updated = characters.find((c) => c.id === characterId)
        if (updated?.picture_url) {
          setAssistantCharacter((prev) => prev?.id === characterId ? updated : prev)
          setAvailableCharacters((prev) =>
            prev.map((c) => c.id === characterId ? updated : c)
          )
          clearInterval(interval)
        }
      } catch {
        // Ignore polling errors
      }
      if (attempts >= maxAttempts) {
        clearInterval(interval)
      }
    }, 5000)
  }

  const existingCustomCharacter = availableCharacters.find((c) => c.is_custom)

  // Billing and the work mail/calendar connection, as one grid; where it sits
  // is OrderedSettingsSections' decision.
  //
  // The row STRETCHES (grid's default — no items-start), so the two cards share
  // a bottom edge and their actions land on one line: Billing pins its action
  // row down with mt-auto, and the Mail card absorbs its slack above the inbox
  // block with mb-auto on the work-account box. Deliberately NOT items-start,
  // which an earlier revision used — it kept each card hugging its content at
  // the cost of a bottom edge that got more ragged with every inbox.
  // Sort is stable, so this only lifts the rows asking for something and
  // otherwise preserves the server's order. The list scrolls from the fourth
  // inbox on, and "the user will spot the half-row" is a weaker guarantee than
  // simply not putting a row that needs action below the fold of its own list.
  const orderedPersonalAccounts = [...connectedPersonalAccounts].sort(
    (a, b) => Number(inboxNeedsAttention(b)) - Number(inboxNeedsAttention(a)),
  )

  // The card is the same in both arms; only its chrome and its home move, and
  // each home owns the chrome it lends. Beside Messaging it wears the catalog
  // cell's, so it is indistinguishable from its neighbours; in the top grid it
  // wears Billing's, the card it is stretched against. `flex flex-col` is
  // needed either way and CATEGORY_CARD_CLASS doesn't carry it (its own
  // children don't need it). `mb-auto` on the work-account box below does the
  // right thing in both: it parks the card's slack above the inbox block so
  // the bottom edge lines up with whatever it is stretched against.
  const mailCard = (
    <div
      {...settingsSectionAnchor('mail-calendar')}
      className={
        mailBesideMessaging
          ? `${CATEGORY_CARD_CLASS} flex flex-col`
          : ACCOUNT_CARD_CLASS
      }
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          {isGoogleUser
            ? <svg className="w-6 h-6" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            : <svg className="w-6 h-6" viewBox="0 0 21 21"><rect x="1" y="1" width="9" height="9" fill="#f25022"/><rect x="1" y="11" width="9" height="9" fill="#00a4ef"/><rect x="11" y="1" width="9" height="9" fill="#7fba00"/><rect x="11" y="11" width="9" height="9" fill="#ffb900"/></svg>
          }
          <h3 className="text-lg font-semibold text-gray-900">Mail & Calendar</h3>
        </div>
        <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full ${
          mailCalendarConnected
            ? 'bg-green-100 text-green-700'
            : 'bg-gray-100 text-gray-500'
        }`}>
          {mailCalendarConnected ? 'Connected' : 'Partial'}
        </span>
      </div>
      {/* Work account — the shaded box carries the visual weight; the
          personal section below stays plain and quieter. */}
      {/* mb-auto puts the card's slack HERE, so everything below lands on
          the bottom edge shared with Billing (which matters when a past-due
          Billing card is the taller of the two). With no slack it resolves
          to 0 and the inbox block keeps its own mt-3 — which is why the
          anchor is mb-auto here rather than mt-auto there, where it would
          fight that mt-3. Invariant: the inbox block must stay the LAST
          child; anything appended after it inherits the bottom edge and
          this has to move. */}
      <div className="rounded-xl bg-gray-50 border border-gray-100 px-4 py-3 mb-auto">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-400 mb-0.5">
          Work account
        </p>
        <p className="text-sm text-gray-700">
          {user?.email}
        </p>
        {!mailCalendarConnected && (
          <>
            <button
              onClick={handleConnectWorkAccount}
              disabled={scopeLoading || workAccountConnecting}
              // role="alert" announces the failure once; aria-describedby is
              // what still associates it with this button afterwards, so a
              // user tabbing back hears the reason rather than just a
              // disabled control.
              aria-describedby={
                workAccountConnectError ? WORK_ACCOUNT_ERROR_ID : undefined
              }
              className="mt-3 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 hover:bg-primary-50 rounded-lg transition-colors border border-primary-200 bg-white disabled:opacity-50"
            >
              {isGoogleUser ? 'Grant mail access' : 'Reconnect Microsoft'}
            </button>
            {/* Both legs above redirect the browser, so on success this is
                never reached. */}
            {workAccountConnectError && (
              <p
                id={WORK_ACCOUNT_ERROR_ID}
                className="text-sm text-red-600 mt-2"
                role="alert"
              >
                {workAccountConnectError}
              </p>
            )}
          </>
        )}
      </div>
      {/* Personal inbox (read + draft only) — nested under the work
          mail/calendar connection; behind the connected-personal-email
          feature flag. The explanatory copy only shows before an account
          is connected (like other integrations). */}
      {personalEmailFeatureEnabled && (
        <div {...settingsSectionAnchor('additional-inboxes')} className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-medium text-gray-500">Additional Inboxes</h4>
              {!personalEmailConnected && (
                <span className="inline-flex items-center rounded-full bg-gray-900 px-2 py-0.5 text-xs font-semibold tracking-wide text-white">
                  NEW
                </span>
              )}
            </div>
            {connectedPersonalAccounts.length > 0 && (
              <button
                onClick={() => void handleOpenPersonalInboxModal()}
                disabled={connectingPersonalEmail}
                className="inline-flex flex-shrink-0 items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50"
              >
                <span aria-hidden="true">+</span>
                Connect
              </button>
            )}
          </div>
          {personalEmailError && (
            <p className="text-sm text-red-600 mb-3" role="alert">{personalEmailError}</p>
          )}
          {connectedPersonalAccounts.length > 0 ? (
            <>
              {/* A divided list rather than a stack of bordered boxes, and
                  capped: from the fourth inbox on this scrolls instead of
                  growing the card. */}
              <ul className={`divide-y divide-gray-100 overflow-y-auto ${INBOX_LIST_MAX_HEIGHT}`}>
                {orderedPersonalAccounts.map((account) => (
                  <ConnectedInboxRow
                    key={account.id}
                    account={account}
                    busy={connectingPersonalEmail}
                    onReconnect={() => {
                      if (account.calendar_provider === IMAP_PROVIDER) {
                        setImapModal({
                          reconnect: { accountId: account.id, email: account.email },
                        })
                        return
                      }
                      void handleConnectPersonalEmail(
                        account.calendar_provider === 'microsoft' ? 'microsoft' : 'google',
                      )
                    }}
                    onDisconnect={() => void handleDisconnectPersonalEmail(account.id)}
                  />
                ))}
              </ul>
              {/* The exact amount lives in the paywall modal (the one spot
                  that blocks connecting until a live price loads) — never
                  hard-code it here. */}
              <p className="mt-2 text-xs text-gray-500">
                Each inbox is billed as its own add-on.
              </p>
            </>
          ) : (
            <button
              onClick={() => void handleOpenPersonalInboxModal()}
              disabled={connectingPersonalEmail}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg border border-gray-200 transition-colors disabled:opacity-50"
            >
              <span aria-hidden="true">+</span>
              Connect another inbox
            </button>
          )}
        </div>
      )}
    </div>
  )

  // Where Billing renders, as one node whose placement the arm decides.
  //
  // Control is the stretched pair at the top of the page. The test arm is
  // Billing alone, full width, below the assistant-waiting card — full width
  // rather than a lone half of the old grid, because a half-width card with
  // dead space beside it reads as a layout that failed to load, and the add-on
  // row above already sets the precedent that a lone card takes the whole row.
  // `mt-6` matches the gap Assistant Email keeps below it.
  const billingSection = mailBesideMessaging ? (
    <div key="billing" className="mt-6">
      <BillingSection />
    </div>
  ) : (
    <div key="billing-and-mail" className="mb-5 grid grid-cols-1 md:grid-cols-2 gap-4">
      <BillingSection />
      {mailCard}
    </div>
  )

  // The catalog, whole or as one of the sidebar's two slices.
  const renderCatalog = (part: CatalogPart) => (
    <IntegrationCategoriesSection key={part === 'all' ? 'integrations' : `integrations-${part}`}
      part={part}
      besideMessagingCard={mailBesideMessaging ? mailCard : undefined}
      assistantName={assistantCharacter?.display_name ?? 'your assistant'}
      assistantPictureUrl={assistantCharacter?.picture_url}
      whatsapp={{
        connected: whatsappStatus?.connected || false,
        loading: whatsappLoading,
        detail: whatsappStatus?.username_only
          ? 'Number hidden'
          : whatsappStatus?.phone_number || undefined,
        onConnect: handleConnectWhatsApp,
        onDisconnect: handleDisconnectWhatsApp,
      }}
      slack={{
        connected: slackStatus?.connected || false,
        loading: slackLoading,
        detail: slackStatus?.workspace_name || undefined,
        onConnect: handleConnectSlack,
        onDisconnect: handleDisconnectSlack,
      }}
      slackPersonal={{
        connected: slackPersonalStatus?.connected || false,
        loading: slackPersonalLoading,
        detail: slackPersonalDetail(slackPersonalStatus),
        onConnect: () => setShowSlackPersonalModal(true),
        onDisconnect: handleDisconnectSlackPersonal,
        onManage: () => setShowSlackPersonalAccessModal(true),
      }}
      teamsAccess={{
        detail: teamsAccessDetail(teamsAccessStatus),
        onSaved: loadTeamsAccessStatus,
      }}
      sms={smsFeatureEnabled ? {
        connected: smsStatus?.connected || false,
        loading: smsLoading,
        detail: smsStatus?.phone_number || undefined,
        onConnect: handleConnectSms,
        onDisconnect: handleDisconnectSms,
      } : null}
      smsPhoneNumber={smsStatus?.twilio_sms_number}
      imessage={{
        connected: imessageStatus?.connected || false,
        loading: imessageLoading,
        detail: imessageStatus?.identifier || undefined,
        onConnect: handleConnectImessage,
        onDisconnect: handleDisconnectImessage,
      }}
      imessagePhoneNumber={imessageStatus?.linq_from_number}
      assistantVoiceNumber={imessageStatus?.assistant_voice_number ?? smsStatus?.assistant_voice_number}
      imessageHandles={imessageStatus?.handles}
      onRemoveImessageHandle={(identifier) => void handleRemoveImessageHandle(identifier)}
      zoom={zoomAvailable ? {
        connected: zoomStatus?.connected || false,
        loading: zoomLoading,
        detail: zoomStatus?.email || undefined,
        onConnect: handleConnectZoom,
        onDisconnect: handleDisconnectZoom,
      } : null}
      googleMeet={isGoogleUser ? {
        connected: scopeStatus?.meet ?? false,
        loading: scopeLoading,
        onConnect: () => void handleConnectScope('meet'),
        onDisconnect: () => {},
      } : null}
      googleDocs={isGoogleUser ? {
        connected: scopeStatus?.docs ?? false,
        loading: scopeLoading,
        onConnect: () => void handleConnectScope('docs'),
        onDisconnect: () => {},
      } : null}
      granola={{
        connected: granolaStatus?.connected || granolaAccounts.instances.length > 0,
        loading: granolaLoading || granolaAccounts.loading,
        onConnect: handleConnectGranola,
        onDisconnect: handleDisconnectGranola,
        instances: granolaAccounts.instances,
        onAddInstance: handleConnectGranola,
        onRemoveInstance: granolaAccounts.remove,
      }}
      fathom={{
        connected: fathomStatus?.connected || fathomAccounts.instances.length > 0,
        loading: fathomLoading || fathomAccounts.loading,
        onConnect: handleConnectFathom,
        onDisconnect: handleDisconnectFathom,
        instances: fathomAccounts.instances,
        onAddInstance: handleConnectFathom,
        onRemoveInstance: fathomAccounts.remove,
      }}
      otter={{
        connected: otterStatus?.connected || otterAccounts.instances.length > 0,
        loading: otterLoading || otterAccounts.loading,
        onConnect: handleConnectOtter,
        onDisconnect: handleDisconnectOtter,
        instances: otterAccounts.instances,
        onAddInstance: handleConnectOtter,
        onRemoveInstance: otterAccounts.remove,
      }}
      wispr={{
        connected: wisprStatus?.connected || wisprAccounts.instances.length > 0,
        loading: wisprLoading || wisprAccounts.loading,
        onConnect: handleConnectWispr,
        onDisconnect: handleDisconnectWispr,
        instances: wisprAccounts.instances,
        onAddInstance: handleConnectWispr,
        onRemoveInstance: wisprAccounts.remove,
      }}
      clickup={{
        connected: clickupStatus?.connected || clickupAccounts.instances.length > 0,
        loading: clickupLoading || clickupAccounts.loading,
        onConnect: handleConnectClickup,
        onDisconnect: handleDisconnectClickup,
        instances: clickupAccounts.instances,
        onAddInstance: handleConnectClickup,
        onRemoveInstance: clickupAccounts.remove,
      }}
      asana={{
        connected: asanaStatus?.connected || asanaAccounts.instances.length > 0,
        loading: asanaLoading || asanaAccounts.loading,
        onConnect: handleConnectAsana,
        onDisconnect: handleDisconnectAsana,
        instances: asanaAccounts.instances,
        onAddInstance: handleConnectAsana,
        onRemoveInstance: asanaAccounts.remove,
      }}
      notion={{
        connected: notionStatus?.connected || notionAccounts.instances.length > 0,
        loading: notionLoading || notionAccounts.loading,
        onConnect: handleConnectNotion,
        onDisconnect: handleDisconnectNotion,
        instances: notionAccounts.instances,
        onAddInstance: handleConnectNotion,
        onRemoveInstance: notionAccounts.remove,
      }}
      hubspot={{
        connected: hubspotStatus?.connected || hubspotAccounts.instances.length > 0,
        loading: hubspotLoading || hubspotAccounts.loading,
        onConnect: handleConnectHubspot,
        onDisconnect: handleDisconnectHubspot,
        instances: hubspotAccounts.instances,
        onAddInstance: handleConnectHubspot,
        onRemoveInstance: hubspotAccounts.remove,
      }}
      salesforce={{
        connected: salesforceStatus?.connected || salesforceAccounts.instances.length > 0,
        loading: salesforceLoading || salesforceAccounts.loading,
        onConnect: handleConnectSalesforce,
        onDisconnect: handleDisconnectSalesforce,
        instances: salesforceAccounts.instances,
        onAddInstance: handleConnectSalesforce,
        onRemoveInstance: salesforceAccounts.remove,
      }}
      honeybook={{
        connected: honeybookStatus?.connected || honeybookAccounts.instances.length > 0,
        loading: honeybookLoading || honeybookAccounts.loading,
        onConnect: handleConnectHoneybook,
        onDisconnect: handleDisconnectHoneybook,
        instances: honeybookAccounts.instances,
        onAddInstance: handleConnectHoneybook,
        onRemoveInstance: honeybookAccounts.remove,
      }}
      attio={{
        connected: attioStatus?.connected || attioAccounts.instances.length > 0,
        loading: attioLoading || attioAccounts.loading,
        onConnect: handleConnectAttio,
        onDisconnect: handleDisconnectAttio,
        instances: attioAccounts.instances,
        onAddInstance: handleConnectAttio,
        onRemoveInstance: attioAccounts.remove,
      }}
      zoho={{
        connected: zohoStatus?.connected || zohoAccounts.instances.length > 0,
        loading: zohoLoading || zohoAccounts.loading,
        // The status's external_user_id is the full MCP URL; show its host.
        detail: hostnameFromUrl(zohoStatus?.external_user_id),
        onConnect: handleConnectZoho,
        onDisconnect: handleDisconnectZoho,
        instances: zohoAccounts.instances,
        onAddInstance: handleConnectZoho,
        onRemoveInstance: zohoAccounts.remove,
      }}
      customIntegrationBuilderEnabled={customIntegrationBuilderEnabled}
      genericPipedreamIntegrationsEnabled
      pendingDriveAccess={pendingDriveConnect}
      pipedreamCatalogBrowseEnabled={pipedreamCatalogBrowseEnabled}
      pendingAppConnectQuery={pendingAppConnectQuery}
      pendingWebhook={pendingWebhook}
    />
  )

  const assistantWaitingSection = (
    <AssistantWaitingCTA
      key="assistant-waiting"
      assistantName={assistantCharacter?.display_name ?? 'your assistant'}
      assistantPictureUrl={assistantCharacter?.picture_url}
      whatsapp={whatsappStatus}
      slack={slackStatus}
      sms={smsStatus}
      imessage={imessageStatus}
    />
  )

  const assistantEmailSection = (
    <div key="assistant-email" className="mt-6 mb-6" {...settingsSectionAnchor('assistant-email')}>
      <AssistantEmailSection />
    </div>
  )

  // The billing page's summary of the inboxes billed as add-ons. The list and its
  // controls stay on the Mail card; this links there.
  const additionalInboxesAddonCard = personalEmailFeatureEnabled ? (
    <div className={ACCOUNT_CARD_CLASS}>
      <h3 className="text-lg font-semibold text-gray-900">Additional inboxes</h3>
      <p className="mt-1 text-sm text-gray-500">
        {connectedPersonalAccounts.length > 0
          ? `${connectedPersonalAccounts.length} connected. Each inbox is billed as its own add-on.`
          : 'Connect another Gmail, Outlook or iCloud inbox. Each one is billed as its own add-on.'}
      </p>
      <a
        href="#additional-inboxes"
        className="mt-3 self-start text-sm font-medium text-primary-600 hover:text-primary-700"
      >
        {connectedPersonalAccounts.length > 0 ? 'Manage inboxes' : 'Connect an inbox'}
      </a>
    </div>
  ) : null

  // The assistant's picture, name and bio. Top of the single page; the "Name
  // and personality" page in the sidebar layout.
  const assistantHero = (
    <div className="mb-5 flex items-center justify-between">
      <div className="flex items-center gap-4">
        {assistantLoading ? (
          <div className="w-12 h-12 rounded-full bg-gray-200 animate-pulse" />
        ) : assistantCharacter?.picture_url ? (
          <img
            src={assistantCharacter.picture_url}
            alt={assistantCharacter.display_name}
            className="w-12 h-12 rounded-full object-cover ring-2 ring-primary-100 shadow-md"
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center ring-2 ring-primary-100 shadow-md">
            <Bot className="w-6 h-6 text-white" />
          </div>
        )}
        <div>
          {assistantCharacter ? (
            <AssistantNameEditor
              character={assistantCharacter}
              onRenamed={setAssistantCharacter}
            />
          ) : (
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Meet your assistant
            </h1>
          )}
          <p className="text-gray-500 mt-0.5">
            {assistantCharacter?.bio ?? 'Your personal AI assistant'}
          </p>
        </div>
      </div>
      {assistantCharacter && (
        <button
          onClick={handleOpenCharacterModal}
          className="px-3 py-1.5 text-sm font-medium text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
        >
          Change
        </button>
      )}
    </div>
  )

  // The Overview's one line per page: what is set up there, where the page
  // knows, else what the page is for.
  const connectedChannels = [
    whatsappStatus?.connected && 'WhatsApp',
    slackStatus?.connected && 'Slack',
    imessageStatus?.connected && 'iMessage',
    smsStatus?.connected && 'SMS',
  ].filter(Boolean).join(', ')
  const extraInboxes = connectedPersonalAccounts.length
  const overviewStatus: Partial<Record<SettingsPageId, string | undefined>> = {
    mail: [user?.email, extraInboxes > 0 && `${extraInboxes} more ${extraInboxes === 1 ? 'inbox' : 'inboxes'}`]
      .filter(Boolean).join(' · '),
    chat: connectedChannels || 'No chat app connected yet',
    assistant: assistantCharacter?.display_name,
  }
  const overviewPage = (
    <>
      <div className="mb-6">{assistantHero}</div>
      <ul className="mb-6 divide-y divide-gray-100 rounded-2xl border border-gray-200 bg-white">
        {SETTINGS_PAGES.filter((page) => page.id !== 'overview').map((page) => (
          <li key={page.id}>
            <a
              href={`#${page.id}`}
              className="flex items-center gap-3 px-5 py-4 hover:bg-gray-50 first:rounded-t-2xl last:rounded-b-2xl"
            >
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-gray-900">{page.label}</span>
                <span className="block truncate text-sm text-gray-500">
                  {overviewStatus[page.id] || page.description}
                </span>
              </span>
              <ChevronRight className="w-4 h-4 flex-shrink-0 text-gray-300" />
            </a>
          </li>
        ))}
      </ul>
    </>
  )

  // A deep link that opens a catalog modal has to land on Apps: the modal is
  // inside that page, and a hidden page shows nothing.
  const requestedSettingsPage = pendingAppConnectQuery || pendingDriveConnect || pendingWebhook
    ? 'apps'
    : null

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      {/* Whatever launched most recently and applies to this visitor, once */}
      <AnnouncementBanner
        viewer={user}
        canConnectInbox={personalEmailFeatureFlag}
        canBuyCredits={purchaseCreditsFlag}
        canUseLocationReminders={locationRemindersFlag}
      />

      {!settingsSidebarEnabled && assistantHero}

      {/* Result of a mobile full-page-redirect connect (GET-8963) */}
      {integrationReturnNotice && (
        <div
          className={`mb-6 p-4 rounded-2xl border ${
            integrationReturnNotice.ok
              ? 'bg-green-50 border-green-200'
              : 'bg-amber-50 border-amber-200'
          }`}
        >
          <div className="flex items-start gap-3">
            {integrationReturnNotice.ok ? (
              <ShieldCheck className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
            )}
            <div className="flex-1">
              <p
                className={`font-semibold ${
                  integrationReturnNotice.ok ? 'text-green-800' : 'text-amber-800'
                }`}
              >
                {integrationReturnNotice.ok
                  ? `${integrationReturnNotice.label} connected`
                  : `Couldn't connect ${integrationReturnNotice.label}`}
              </p>
              {!integrationReturnNotice.ok && (
                <>
                  {integrationReturnNotice.reason && (
                    <p className="text-sm text-amber-700 mt-0.5">
                      {integrationReturnNotice.reason}
                    </p>
                  )}
                  <p className="text-sm text-amber-700 mt-0.5">
                    Please try connecting again.
                  </p>
                </>
              )}
            </div>
            <button
              onClick={() => setIntegrationReturnNotice(null)}
              className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Partial Google consent: user approved some but not all requested scopes */}
      {incompleteScopeClusters.length > 0 && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-2xl">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-amber-800">
                {onlyOneIncompleteScope
                  ? `${incompleteScopeLabel} is not fully connected`
                  : 'Some permissions are not fully connected'}
              </p>
              <p className="text-sm text-amber-700 mt-1">
                You approved part of Google&apos;s permission screen, but Catch needs the full
                set to use {onlyOneIncompleteScope ? incompleteScopeLabel : 'these'}. Open it
                again and approve every box.
              </p>
              <p className="flex items-center gap-1.5 text-xs text-amber-700/90 mt-2">
                <ShieldCheck className="w-4 h-4 flex-shrink-0" />
                <span>
                  Curious what Catch can access? Read our{' '}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline font-medium hover:text-amber-900"
                  >
                    Privacy Policy
                  </a>{' '}
                  and{' '}
                  <a
                    href="/terms-new"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline font-medium hover:text-amber-900"
                  >
                    Terms
                  </a>
                  .
                </span>
              </p>
              <button
                onClick={() => void handleRetryIncompleteScopes()}
                disabled={scopeLoading}
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-amber-500 hover:bg-amber-600 rounded-lg transition-colors disabled:opacity-50"
              >
                Finish connecting
              </button>
            </div>
          </div>
        </div>
      )}

      {settingsSidebarEnabled ? (
        <SettingsNav
          requestedPage={requestedSettingsPage}
          pages={{
            overview: overviewPage,
            mail: <div className="mb-6">{mailCard}</div>,
            chat: (
              <>
                {assistantWaitingSection}
                {renderCatalog('messaging')}
                {assistantEmailSection}
              </>
            ),
            apps: <div className="mb-6">{renderCatalog('apps')}</div>,
            assistant: <div className="mb-6">{assistantHero}</div>,
            permissions: (
              <>
                <AutomaticApprovalsBlock />
                <TextingContactsBlock />
              </>
            ),
            phone: (
              <>
                <DedicatedNumberBlock />
                <IphoneActionButtonBlock />
              </>
            ),
            billing: (
              <>
                <div className="mb-6"><BillingSection /></div>
                {additionalInboxesAddonCard && <div className="mb-6">{additionalInboxesAddonCard}</div>}
                <PurchaseCreditsBlock show={purchaseCreditsFlag === true} />
              </>
            ),
          }}
        />
      ) : (
      <OrderedSettingsSections
        billing={billingSection}
        billingBelowWaiting={mailBesideMessaging}
        secondary={(
          <SecondarySettingsSections
            key="secondary"
            showPurchaseCredits={purchaseCreditsFlag === true}
          />
        )}
        integrations={renderCatalog('all')}
        assistantWaiting={assistantWaitingSection}
        assistantEmail={assistantEmailSection}
      />
      )}

      <ReferralStrip />

      <ReferralLinkModal
        isOpen={showReferralSpotlight}
        onClose={() => setShowReferralSpotlight(false)}
      />

      <WhatsAppPairingModal
        isOpen={showWhatsAppModal}
        onClose={() => setShowWhatsAppModal(false)}
        onSuccess={handleWhatsAppSuccess}
      />

      <SlackPairingModal
        isOpen={showSlackPersonalModal}
        onClose={() => setShowSlackPersonalModal(false)}
        onSuccess={async () => {
          await loadSlackPersonalStatus()
          // Land straight in the picker: an opt-in grant with nothing ticked
          // reads nothing, which is the safe default but not a useful one.
          setShowSlackPersonalAccessModal(true)
        }}
        flow={SLACK_PERSONAL_FLOW}
      />
      <SlackPersonalAccessModal
        isOpen={showSlackPersonalAccessModal}
        onClose={() => setShowSlackPersonalAccessModal(false)}
        onDisconnect={handleDisconnectSlackPersonal}
        onSaved={loadSlackPersonalStatus}
      />
      <SlackPairingModal
        isOpen={showSlackModal}
        onClose={() => setShowSlackModal(false)}
        onSuccess={handleSlackSuccess}
      />

      {smsFeatureEnabled && (
        <SMSPairingModal
          isOpen={showSmsModal}
          onClose={() => setShowSmsModal(false)}
          onSuccess={handleSmsSuccess}
        />
      )}

      <IMessagePairingModal
        isOpen={showImessageModal}
        onClose={() => setShowImessageModal(false)}
        onSuccess={handleImessageSuccess}
      />

      {zoomAvailable && (
        <ZoomPairingModal
          isOpen={showZoomModal}
          onClose={() => setShowZoomModal(false)}
          onSuccess={handleZoomSuccess}
        />
      )}

      <GranolaPairingModal
        isOpen={showGranolaModal}
        onClose={() => setShowGranolaModal(false)}
        onSuccess={handleGranolaSuccess}
        existingLabels={granolaAccounts.instances.map((i) => i.label)}
      />

      <FathomPairingModal
        isOpen={showFathomModal}
        onClose={() => setShowFathomModal(false)}
        onSuccess={handleFathomSuccess}
        existingLabels={fathomAccounts.instances.map((i) => i.label)}
      />

      <WisprPairingModal
        isOpen={showWisprModal}
        onClose={() => setShowWisprModal(false)}
        onSuccess={handleWisprSuccess}
        existingLabels={wisprAccounts.instances.map((i) => i.label)}
      />

      <OtterPairingModal
        isOpen={showOtterModal}
        onClose={() => setShowOtterModal(false)}
        onSuccess={handleOtterSuccess}
        existingLabels={otterAccounts.instances.map((i) => i.label)}
      />

      <ClickUpPairingModal
        isOpen={showClickupModal}
        onClose={() => setShowClickupModal(false)}
        onSuccess={handleClickupSuccess}
        existingLabels={clickupAccounts.instances.map((i) => i.label)}
      />

      <AsanaPairingModal
        isOpen={showAsanaModal}
        onClose={() => setShowAsanaModal(false)}
        onSuccess={handleAsanaSuccess}
        existingLabels={asanaAccounts.instances.map((i) => i.label)}
      />

      <NotionPairingModal
        isOpen={showNotionModal}
        onClose={() => setShowNotionModal(false)}
        onSuccess={handleNotionSuccess}
        existingLabels={notionAccounts.instances.map((i) => i.label)}
      />

      <HubSpotPairingModal
        isOpen={showHubspotModal}
        onClose={() => setShowHubspotModal(false)}
        onSuccess={handleHubspotSuccess}
        existingLabels={hubspotAccounts.instances.map((i) => i.label)}
      />

      <SalesforcePairingModal
        isOpen={showSalesforceModal}
        onClose={() => setShowSalesforceModal(false)}
        onSuccess={handleSalesforceSuccess}
        existingLabels={salesforceAccounts.instances.map((i) => i.label)}
      />

      <HoneyBookPairingModal
        isOpen={showHoneybookModal}
        onClose={() => setShowHoneybookModal(false)}
        onSuccess={handleHoneybookSuccess}
        existingLabels={honeybookAccounts.instances.map((i) => i.label)}
      />

      <AttioPairingModal
        isOpen={showAttioModal}
        onClose={() => setShowAttioModal(false)}
        onSuccess={handleAttioSuccess}
        existingLabels={attioAccounts.instances.map((i) => i.label)}
      />

      <DiscoveredUrlPairingModal
        isOpen={showZohoModal}
        onClose={() => setShowZohoModal(false)}
        onSuccess={handleZohoSuccess}
        existingLabels={zohoAccounts.instances.map((i) => i.label)}
        name="zoho"
        displayName="Zoho"
        urlLabel="Zoho MCP server URL"
        urlPlaceholder="https://crm-data-metadata-XXXXXXXX.zohomcp.com/mcp/<API_KEY>/message"
        service={zohoService}
        events={{
          modalOpened: OnboardingEvents.ZOHO_MODAL_OPENED,
          urlSubmitted: OnboardingEvents.ZOHO_URL_SUBMITTED,
          connected: OnboardingEvents.ZOHO_CONNECTED,
          connectionFailed: OnboardingEvents.ZOHO_CONNECTION_FAILED,
          modalClosed: OnboardingEvents.ZOHO_MODAL_CLOSED,
        }}
        instructions={(
          <p>
            Run your own Zoho MCP server at{' '}
            <a
              href="https://mcp.zoho.com"
              target="_blank"
              rel="noreferrer"
              className="text-primary-600 hover:underline"
            >
              mcp.zoho.com
            </a>
            , pick the Zoho apps you want (CRM, Desk, Mail, Books, Projects, ...),
            then paste the server URL below. You'll be sent to Zoho to authorize
            access.
          </p>
        )}
      />

      {/* Character Selection Modal */}
      {/* Personal Inbox explainer — the single quiet button opens this; the
          provider choice lives here, and picking one starts its OAuth
          connect. All the how-it-works copy stays out of the card. */}
      <ImapConnectModal
        isOpen={imapModal !== null}
        reconnect={imapModal?.reconnect ?? null}
        onClose={() => setImapModal(null)}
        onConnected={handleImapConnected}
      />

      {showPersonalInboxModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-lg font-semibold text-gray-900">
                Connect an additional inbox
              </h3>
              <span className="inline-flex items-center rounded-full bg-gray-900 px-2 py-0.5 text-xs font-semibold tracking-wide text-white">
                NEW
              </span>
            </div>
            <p className="text-sm text-gray-500 mb-3">
              Connect another Gmail or Outlook inbox — personal or business —
              so {assistantCharacter?.display_name ?? 'your assistant'} can search and tidy it when
              you ask, keep its commitments in mind when scheduling, and
              handle replies and calendar invites from your own address.
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Nothing goes out without you — {assistantCharacter?.display_name ?? 'your assistant'}{' '}
              asks for your approval before every send from this inbox.
            </p>
            {/* Paywall (GET-8966): the price lives ONLY here, and it must be
                unmissable. Until a live price is loaded, connecting is
                blocked — never let someone connect without seeing the cost. */}
            {personalInboxPrice === 'unavailable' ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 mb-4">
                <p className="text-sm text-amber-800">
                  We couldn't load the add-on price right now, so connecting is
                  paused. Please try again in a bit.
                </p>
              </div>
            ) : addonOptInRequired && personalInboxPrice ? (
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 mb-4">
                <p className="text-sm font-semibold text-gray-900">
                  {`${personalInboxPrice.price_formatted}/${personalInboxPrice.interval}`} per inbox
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  {describeAddonChargeStart(personalInboxPrice, personalInboxSubscription)}{' '}
                  Disconnect an inbox anytime and its billing stops with a
                  prorated credit.
                </p>
                <label className="mt-3 flex items-start gap-2 text-sm text-gray-900 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={addonOptedIn}
                    onChange={(e) => {
                      setAddonOptedIn(e.target.checked)
                      trackEvent(OnboardingEvents.PERSONAL_INBOX_ADDON_OPT_IN_TOGGLED, {
                        checked: e.target.checked,
                      })
                    }}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span>{addonOptInLabel(personalInboxPrice)}</span>
                </label>
              </div>
            ) : (
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 mb-4">
                <p className="text-sm font-semibold text-gray-900">
                  {personalInboxPrice
                    ? `${personalInboxPrice.price_formatted}/${personalInboxPrice.interval}`
                    : '…'}{' '}
                  per inbox
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  Each inbox you connect is its own add-on — two inboxes, two
                  add-ons. Billed by Stripe on your existing Catch
                  subscription. Disconnect an inbox anytime — its billing
                  stops with a prorated credit.
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <button
                onClick={() => {
                  trackEvent(OnboardingEvents.PERSONAL_INBOX_PAYWALL_CONTINUE, {
                    provider: 'google',
                    explicit_opt_in: addonOptInRequired,
                  })
                  setShowPersonalInboxModal(false)
                  void handleConnectPersonalEmail('google')
                }}
                disabled={personalInboxConnectLocked}
                className="flex flex-col items-center justify-center gap-2 p-4 rounded-xl bg-white border border-gray-200 hover:border-primary-300 hover:shadow-sm transition-colors disabled:opacity-60"
              >
                <div className="w-8 h-8 flex items-center justify-center">
                  <svg className="w-6 h-6" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                </div>
                <span className="text-sm font-medium text-gray-900">Google</span>
              </button>
              <button
                onClick={() => {
                  trackEvent(OnboardingEvents.PERSONAL_INBOX_PAYWALL_CONTINUE, {
                    provider: 'microsoft',
                    explicit_opt_in: addonOptInRequired,
                  })
                  setShowPersonalInboxModal(false)
                  void handleConnectPersonalEmail('microsoft')
                }}
                disabled={personalInboxConnectLocked}
                className="flex flex-col items-center justify-center gap-2 p-4 rounded-xl bg-white border border-gray-200 hover:border-primary-300 hover:shadow-sm transition-colors disabled:opacity-60"
              >
                <div className="w-8 h-8 flex items-center justify-center">
                  <svg className="w-6 h-6" viewBox="0 0 21 21"><rect x="1" y="1" width="9" height="9" fill="#f25022"/><rect x="1" y="11" width="9" height="9" fill="#00a4ef"/><rect x="11" y="1" width="9" height="9" fill="#7fba00"/><rect x="11" y="11" width="9" height="9" fill="#ffb900"/></svg>
                </div>
                <span className="text-sm font-medium text-gray-900">Microsoft</span>
              </button>
              {imapPersonalInboxEnabled && (
                <button
                  onClick={() => {
                    trackEvent(OnboardingEvents.PERSONAL_INBOX_PAYWALL_CONTINUE, {
                      provider: IMAP_PROVIDER,
                      explicit_opt_in: addonOptInRequired,
                    })
                    setShowPersonalInboxModal(false)
                    setImapModal({ reconnect: null })
                  }}
                  disabled={personalInboxConnectLocked}
                  className="col-span-2 flex items-center justify-center gap-2 p-3 rounded-xl bg-white border border-gray-200 hover:border-primary-300 hover:shadow-sm transition-colors disabled:opacity-60"
                >
                  <span className="text-sm font-medium text-gray-900">
                    iCloud or another inbox
                    <span className="block text-xs font-normal text-gray-500">
                      Yahoo, Fastmail and any IMAP inbox
                    </span>
                  </span>
                </button>
              )}
            </div>
            {/* GET-19037: Google's add-account step fails for most Chrome-on-iOS
                users and never returns to us, so say so before they tap. */}
            {isChromeOnIOS() && (
              <p className="text-xs text-gray-600 mb-4">
                Using Chrome on iPhone or iPad? Open this page in Safari to
                connect a Gmail inbox. Google often shows an error when you add
                a second account in Chrome.
              </p>
            )}
            <div className="flex justify-end">
              <button
                onClick={() => setShowPersonalInboxModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showCharacterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] sm:max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-2xl font-bold text-gray-900">Choose Your Assistant</h2>
              <p className="text-gray-600 mt-1">Select which character you'd like to interact with</p>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <div className="grid gap-4">
                {availableCharacters.map((character) => (
                  <div key={character.id} className="relative">
                    <button
                      onClick={() => void handleChangeCharacter(character.id)}
                      disabled={changingCharacter || deletingCustomCharacter}
                      className={`w-full p-4 border-2 rounded-xl transition-colors text-left ${
                        assistantCharacter?.id === character.id
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
                      } ${changingCharacter || deletingCustomCharacter ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <div className="flex items-center gap-4">
                        {character.picture_url ? (
                          <img
                            src={character.picture_url}
                            alt={character.name}
                            className="w-16 h-16 rounded-full object-cover ring-2 ring-white shadow-md"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-200 to-primary-300 flex items-center justify-center ring-2 ring-white shadow-md">
                            <span className="text-2xl font-bold text-primary-700">{character.name.charAt(0)}</span>
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-lg font-bold text-gray-900">{character.name}</h3>
                            {assistantCharacter?.id === character.id && (
                              <span className="px-2 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full">
                                Current
                              </span>
                            )}
                            {character.is_custom && (
                              <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-700 rounded-full">
                                Custom
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">{character.bio}</p>
                        </div>
                      </div>
                    </button>
                    {character.is_custom && (
                      <div className="absolute top-3 right-3 flex gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            trackEvent(OnboardingEvents.ASSISTANT_CUSTOM_FORM_OPENED, { mode: 'edit', source: 'tile' })
                            setShowCustomCharacterForm(true)
                          }}
                          className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); void handleDeleteCustomCharacter() }}
                          disabled={deletingCustomCharacter}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-between flex-shrink-0">
              <button
                onClick={() => {
                  trackEvent(OnboardingEvents.ASSISTANT_CUSTOM_FORM_OPENED, {
                    mode: existingCustomCharacter ? 'edit' : 'create',
                    source: 'footer',
                  })
                  setShowCustomCharacterForm(true)
                }}
                disabled={changingCharacter}
                className="px-4 py-2 text-sm font-medium text-primary-600 hover:bg-primary-50 rounded-lg transition-colors border border-primary-200 flex items-center gap-2"
              >
                {existingCustomCharacter ? (
                  <><Pencil className="w-4 h-4" /> Edit Custom</>
                ) : (
                  <><Plus className="w-4 h-4" /> Create Custom</>
                )}
              </button>
              <button
                onClick={handleCloseCharacterModal}
                disabled={changingCharacter}
                className="px-6 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showCustomCharacterForm && (
        <CustomCharacterForm
          onSuccess={handleCustomCharacterCreated}
          onClose={() => setShowCustomCharacterForm(false)}
          existingCharacter={existingCustomCharacter}
          onPictureChanged={handleCharacterPictureChanged}
        />
      )}
    </div>
  )
}
